import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import {
  AlertTriangle,
  Anchor,
  Camera,
  Check,
  ChevronDown,
  ChevronUp,
  Crosshair,
  Download,
  ExternalLink,
  Info,
  MapPinOff,
  MapPinned,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Pencil,
  PersonStanding,
  Ruler,
  Trash2,
  X, Sprout} from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useZEElementTypes } from "@/features/ze_tenders/hooks";
import { elementSwatchSvg, findOwnershipOption, lineStyleFor, objectSvg, ownershipCodeColor } from "@/features/ze_tenders/utils";
import { groupOwnersForDisplay } from "../ownerDisplay";
import { currentWpwByOwner, ownerAccessCell } from "../wpwOwnerStatus";
import { RouteUploadPanel } from "@/features/contractor_led/components/RouteUploadPanel";
import type { LedFile } from "@/features/contractor_led/types";
import type { ZEElementType } from "@/features/ze_tenders/types";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { usePersistentToggle } from "@/hooks/usePersistentToggle";
import { useSignedWmsUrl } from "@/features/tasks/hooks/useSignedWmsUrl";
import {
  ClosedAreasLayer,
  createGugikLayers,
  GddkiaDataLayer,
  geoportalParcelUrl,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
} from "@/components/map/gugik";
import { isEmptyGeometry } from "@/components/map/geometry";
import {
  useCreateVariant,
  useDeleteVariant,
  useDxfLayerPresets,
  useDxfLayers,
  useMzOrders,
  useParcels,
  useProceedings,
  useProjectEvents,
  useSetBindingVariant,
  useTaskPolling,
  useUploadRouteVersion,
  useVariants,
  useWpwHandovers,
} from "../hooks";
import {createMdcpPoint, createParcel, fetchParcelUldk, getUldkAtPoint, importTenderVariant, rebuildVersionPreview, recomputeVersion, refetchOceanParcels, resolveParcelConflicts, saveMdcpBuffer, createMdcpBuffer, updateMdcpPoint, validateDxf, type UldkPointInfo, getOdrolnienieFootprints } from "../api";
import { ParcelConflictsModal } from "./ParcelConflictsModal";
import { WpwStaleAfterRouteModal } from "./WpwStaleAfterRouteModal";
import type { ParcelConflictDecision, ParcelConflictGroup, WpwRouteStalePackage } from "../types";
import { getGeoportalUrl } from "@/features/ze_tenders/api";
import { getFieldPhotos } from "../api";
import type { FieldPhoto } from "../types";
import { MdcpSectionCard, mdcpQueryKey, useMdcpPanel } from "./MdcpSectionCard";
import { buildFieldPhotoLightbox, buildMdcpLightbox, MdcpPhotoLightbox } from "./MdcpPhotoLightbox";
import {
  mdcpPhotoBadgeIcon,
  mdcpPhotoBadgeTooltip,
  mdcpPointPhotosPopupHtml,
  mdcpPointsWithPhotos,
} from "./mdcpMap";
import { CreateMzOrderModal } from "./mz/CreateMzOrderModal";
import { MzStatusBadge } from "./mz/MzStatusBadge";
import { useZEVariants } from "@/features/ze_tenders/hooks";
import { FormatkiSectionCard } from "./formatki/FormatkiSectionCard";
import {
  type DxfLayerStat,
  type DxfValidationReport,
  type GeoJsonGeometry,
  type MdcpPhotoPoint,
  type Parcel,
  type ParcelStatus,
  type PowerProject,
  type Proceeding,
  type RouteVariant,
  type RouteVersion,
  type WpwHandover,
  PARCEL_STATUS_LABEL,
  WPW_DECLARED_STATUS_COLOR,
  WPW_DECLARED_STATUS_LABEL,
} from "../types";
import { apiErr, fmtDate, fmtDateTime, fmtTime, isPrivateSideParcel, isProjectParcel } from "../utils";
import { PARCEL_NOTIFICATION_FILL, PARCEL_SPARE_FILL, SectionCard, parcelDotColor, parcelFillColor, parcelFillKind, type ParcelFillKind } from "./ui";
import { labelCandidates, ringArea, type Rings } from "./parcelLabelPlacement";
import { EditRouteVersionModal } from "./EditRouteVersionModal";
import { DeleteRouteVersionModal } from "./DeleteRouteVersionModal";
import { WpwConversationModal } from "./WpwConversationModal";

const isDxf = (f: File) => f.name.toLowerCase().endsWith(".dxf");

// Persists the opt-in "monit przed wysłaniem" (pre-upload layer check) checkbox.
const DXF_MONIT_STORAGE_KEY = "powerProjects.dxfUploadMonit";

// Persists the "pokazuj punkty zdjęciowe (MDCP) na mapie" toggle — per browser, all projects.
const MDCP_POINTS_VISIBLE_STORAGE_KEY = "powerProjects.showMdcpPoints";

// Persists the „Zdjęcia z terenu" (geotagged result-file photos) layer toggle. Off by
// default: it is an acceptance aid, not part of the everyday route view.
const FIELD_PHOTOS_VISIBLE_STORAGE_KEY = "powerProjects.showFieldPhotos";

// Remembers the last map viewport (center + zoom) per project for the current browser
// session. Entering the "Mapa / Trasa" tab remounts MapTab, which would otherwise snap
// the view back to the project footprint; restoring the saved viewport lets the user
// return to exactly where they had panned/zoomed after switching tabs without a page
// reload. Session-scoped on purpose: a full refresh or a new tab starts from the auto-fit.
const MAP_VIEWPORT_STORAGE_PREFIX = "powerProjects.mapViewport.";

type MapViewport = { lat: number; lng: number; zoom: number };

function loadMapViewport(projectId: string): MapViewport | null {
  try {
    const raw = sessionStorage.getItem(MAP_VIEWPORT_STORAGE_PREFIX + projectId);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<MapViewport>;
    if (
      typeof v.lat === "number" &&
      typeof v.lng === "number" &&
      typeof v.zoom === "number"
    ) {
      return { lat: v.lat, lng: v.lng, zoom: v.zoom };
    }
  } catch {
    // Malformed JSON or storage unavailable (private mode) — fall back to auto-fit.
  }
  return null;
}

// Remembers the display mode (Uznane / Surowy DXF) the same way as the viewport: per project,
// for the current browser session. Scoped that tightly on purpose — "Surowy DXF" only shows
// anything where that version has a built preview, so the choice must never follow the user
// into another project (where it would greet them with "Brak podglądu DXF") or outlive a reload.
const MAP_MODE_STORAGE_PREFIX = "powerProjects.mapMode.";

type MapMode = "curated" | "raw";

function loadMapMode(projectId: string): MapMode | null {
  try {
    const v = sessionStorage.getItem(MAP_MODE_STORAGE_PREFIX + projectId);
    return v === "curated" || v === "raw" ? v : null;
  } catch {
    return null;
  }
}

function saveMapMode(projectId: string, mode: MapMode): void {
  try {
    sessionStorage.setItem(MAP_MODE_STORAGE_PREFIX + projectId, mode);
  } catch {
    // Ignore write failures (quota / private mode) — persistence is best-effort.
  }
}

function saveMapViewport(projectId: string, map: L.Map): void {
  try {
    const c = map.getCenter();
    const payload: MapViewport = { lat: c.lat, lng: c.lng, zoom: map.getZoom() };
    sessionStorage.setItem(
      MAP_VIEWPORT_STORAGE_PREFIX + projectId,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore write failures (quota / private mode) — persistence is best-effort.
  }
}

/** A route version as the map tab lists it: flattened out of its variant, carrying the
 * variant facts the timeline, the "Wersja trasy" card and the delete modal need. */
type TimelineVersion = RouteVersion & {
  variantLabel: string;
  variantBinding?: boolean;
  // RouteVariant.can_delete — gates the delete control per version.
  variantCanDelete?: boolean;
};

function currentVersionOf(
  variants: { versions: RouteVersion[]; is_binding: boolean }[] | undefined,
): RouteVersion | null {
  // Prefer the binding variant — its current version is the one that drives
  // statuses/sprawy, so it's what should show by default when the map opens.
  const ordered = [...(variants ?? [])].sort(
    (a, b) => Number(b.is_binding) - Number(a.is_binding),
  );
  for (const v of ordered) {
    const cur = v.versions.find((ver) => ver.is_current) || v.versions[0];
    if (cur) return cur;
  }
  return null;
}

// Poland's WGS84 bounding box (padded). A parcel whose GeoJSON (already 4326) falls
// outside it landed in the ocean via the legacy SRID mistag — offer a re-fetch then.
// Które działki należą do projektu na mapie: te pod bieżącą trasą, dodane ręcznie oraz
// te oznaczone ręcznie jako WYKORZYSTYWANE (usage_override) — ostatnie to działki, których
// automat nie złapał (trasa narysowana po granicy, róg działki), więc muszą rysować się
// dokładnie tak samo jak działki wykryte z DXF-a. Reguła mieszka w utils.isProjectParcel —
// ta sama, którą czytają zbiór WPW, kreator wniosków i lista działek.

const _PL_LNG: readonly [number, number] = [13, 25];
const _PL_LAT: readonly [number, number] = [48, 55.5];
function geometryOutsidePoland(geom: { coordinates?: unknown } | null): boolean {
  let c: unknown = geom?.coordinates;
  while (Array.isArray(c) && Array.isArray(c[0])) c = c[0]; // drill to the first [lng, lat]
  if (!Array.isArray(c) || typeof c[0] !== "number" || typeof c[1] !== "number") return false;
  const [lng, lat] = c as [number, number];
  return !(lng >= _PL_LNG[0] && lng <= _PL_LNG[1] && lat >= _PL_LAT[0] && lat <= _PL_LAT[1]);
}

// ── recognized DXF object markers — mirror of the ZE tender map's symbol language ──
// The tender map draws each object as an SVG symbol (shape = kind, colour = type). MapObject
// only carries a free-form `semantic_type` (DXF presets define arbitrary strings), so we derive a
// stable colour by hashing it and a shape from a few keyword matches, defaulting to a circle.
// Palette mirrors ze_tenders `PALETTE` so both maps read in the same colours.
const OBJECT_PALETTE = [
  "#dc2626", "#2563eb", "#7c3aed", "#0d9488", "#ea580c", "#65a30d", "#0891b2", "#db2777",
];
function objectColor(semantic: string): string {
  let h = 0;
  for (let i = 0; i < semantic.length; i++) h = (h * 31 + semantic.charCodeAt(i)) >>> 0;
  return OBJECT_PALETTE[h % OBJECT_PALETTE.length];
}
// Mirrors backend `_is_demolition_semantic` (apps/geo/services/dxf.py): a line semantic
// marks demolition when it mentions likwidacja / demontaż ("demonta" covers demontaż/
// demontaz). DXF presets use arbitrary strings, so demolition must be recognized by
// keyword — a matching ZEElementType row may not exist.
function isDemolitionSemantic(semantic: string | null | undefined): boolean {
  const s = (semantic ?? "").toLowerCase();
  return s.includes("likwidacja") || s.includes("demonta");
}
type ObjectShape = "circle" | "square" | "diamond" | "triangle";
function objectShape(semantic: string): ObjectShape {
  const s = semantic.toLowerCase();
  if (/stacj|trafo|transformator|gpz|rozdziel/.test(s)) return "square";
  if (/z[łl][aą]cz|mufa|szafk|roz[łl][aą]cznik|odga[łl][eę]/.test(s)) return "diamond";
  if (/s[łl]up|podpor/.test(s)) return "triangle";
  return "circle";
}
// Tender-style SVG symbol markup — shared by the map marker (divIcon) and the legend
// swatch, so both always show the same hash-derived symbol.
export function objectSymbolSvg(semantic: string, size = 16): string {
  const c = size / 2;
  const color = objectColor(semantic);
  let el: string;
  switch (objectShape(semantic)) {
    case "square":
      el = `<rect x="2" y="2" width="${size - 4}" height="${size - 4}" rx="1"/>`;
      break;
    case "diamond":
      el = `<polygon points="${c},1.5 ${size - 1.5},${c} ${c},${size - 1.5} 1.5,${c}"/>`;
      break;
    case "triangle":
      el = `<polygon points="${c},2 ${size - 2},${size - 2} 2,${size - 2}"/>`;
      break;
    default:
      el = `<circle cx="${c}" cy="${c}" r="${c - 2}"/>`;
  }
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg"><g fill="${color}" stroke="#fff" stroke-width="1.5" stroke-linejoin="round">${el}</g></svg>`;
}
// The custom className drops Leaflet's default white div-icon box (same trick as the
// ZE map's "ze-marker-icon"), so only the SVG shows.
export function objectIcon(semantic: string, size = 16): L.DivIcon {
  const c = size / 2;
  return L.divIcon({
    html: objectSymbolSvg(semantic, size),
    className: "pp-object-icon",
    iconSize: [size, size],
    iconAnchor: [c, c],
  });
}
// Fallback style for typed lines without a ZEElementType row, derived from the slug
// itself. Mirrors the seeded palette's visual language (0031_reseed_ze_element_types):
// colour = voltage (nN blue / SN red), pale = likwidacja/demontaż, dash "6 5" =
// napowietrzna. Slugs without a voltage token keep the legacy route colours
// (red construction / blue dashed demolition).
const NN_HUE = "#2563eb", SN_HUE = "#dc2626";
const NN_PALE = "#93c5fd", SN_PALE = "#fca5a5";
export function fallbackLineStyle(
  semantic: string | null | undefined,
): { color: string; weight: number; dashArray?: string } {
  const s = (semantic ?? "").toLowerCase();
  const demolition = isDemolitionSemantic(s);
  const voltage = /(^|_)sn(_|$)/.test(s) ? "sn" : /(^|_)nn(_|$)/.test(s) ? "nn" : null;
  const color =
    voltage === "sn"
      ? demolition ? SN_PALE : SN_HUE
      : voltage === "nn"
        ? demolition ? NN_PALE : NN_HUE
        : demolition ? "#2563eb" : "#dc2626";
  const overhead = s.includes("napowietrzn");
  return {
    color,
    weight: 3,
    dashArray: overhead ? "6 5" : demolition && !voltage ? "6 4" : undefined,
  };
}
// Legend swatch drawn with exactly the style drawRoute paints the line with.
export function fallbackLineSwatch(semantic: string | null | undefined): string {
  const st = fallbackLineStyle(semantic);
  const dash = st.dashArray ? ` stroke-dasharray="${st.dashArray}"` : "";
  return `<svg width="24" height="12" viewBox="0 0 24 12" xmlns="http://www.w3.org/2000/svg"><line x1="1" y1="6" x2="23" y2="6" stroke="${st.color}" stroke-width="3"${dash}/></svg>`;
}

// ── MDCP photo point marker: numbered circle + a direction arrow rotated by azimuth
// (0° = north, clockwise). Pink = to order, purple = at the surveyor, green = done.
function mdcpIcon(p: MdcpPhotoPoint, selected: boolean): L.DivIcon {
  const s = 36;
  const c = s / 2;
  const color = p.is_done ? "#16a34a" : p.order_link ? "#7c3aed" : "#db2777";
  const ring = selected
    ? `<circle cx="${c}" cy="${c}" r="${c - 2}" fill="none" stroke="${color}" stroke-width="1.5" stroke-dasharray="3 3"/>`
    : "";
  return L.divIcon({
    html:
      `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg">` +
      `<g transform="rotate(${p.azimuth_deg}, ${c}, ${c})">` +
      `<line x1="${c}" y1="${c}" x2="${c}" y2="6" stroke="${color}" stroke-width="2.5"/>` +
      `<polygon points="${c},1 ${c - 4.5},8.5 ${c + 4.5},8.5" fill="${color}"/>` +
      `</g>${ring}` +
      `<circle cx="${c}" cy="${c}" r="9" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
      `<text x="${c}" y="${c + 3.5}" text-anchor="middle" font-size="10" font-weight="700" fill="#fff">${p.number}</text>` +
      `</svg>`,
    className: "pp-object-icon",
    iconSize: [s, s],
    iconAnchor: [c, c],
  });
}

// ── „Zdjęcia z terenu": camera pin coloured by the photo's review state — green =
// accepted, red = rejected (incl. auto-rejected screenshots), amber = pending / suspect.
function fieldPhotoIcon(p: FieldPhoto): L.DivIcon {
  const s = 26;
  const color =
    p.status === "accepted" ? "#16a34a" : p.status === "rejected" ? "#dc2626" : "#d97706";
  return L.divIcon({
    html:
      `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">` +
      `<circle cx="12" cy="12" r="11" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
      `<path d="M7 9.5A1.5 1.5 0 0 1 8.5 8h1.2l.9-1.5h2.8l.9 1.5h1.2A1.5 1.5 0 0 1 17 9.5V15a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 15z" fill="none" stroke="#fff" stroke-width="1.4"/>` +
      `<circle cx="12" cy="12.3" r="2.3" fill="none" stroke="#fff" stroke-width="1.4"/>` +
      `</svg>`,
    className: "pp-object-icon",
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
    popupAnchor: [0, -s / 2],
  });
}

function fieldPhotoPopupHtml(p: FieldPhoto): string {
  const when = p.taken_at ? fmtDateTime(p.taken_at) : "brak daty w EXIF";
  const status =
    p.status === "accepted" ? "zaakceptowane" : p.status === "rejected" ? "odrzucone" : "oczekuje";
  const near = p.nearest
    ? `${p.nearest.distance_m.toLocaleString("pl-PL", { maximumFractionDigits: 0 })} m od ` +
      `${p.nearest.kind === "parcel" ? "działki" : "obiektu"} ${esc(p.nearest.label)}` +
      (p.scoped ? "" : ' <span style="color:#9ca3af">(zakres zamówienia nieznany — najbliższa działka projektu)</span>')
    : '<span style="color:#9ca3af">brak działek/obiektów odniesienia</span>';
  // Miniatura jako przycisk, nie link do nowej karty: na tej warstwie KAŻDE zdjęcie
  // otwiera się tak samo — kliknięciem w miniaturę, na pełnym ekranie (data-photo-id
  // podpina popupopen mapy).
  const img = p.file_url
    ? `<button type="button" data-photo-id="${esc(p.id)}" title="Kliknij — podgląd na pełnym ekranie" ` +
      `style="display:block;padding:0;border:0;background:none;cursor:pointer;line-height:0;margin-bottom:6px">` +
      `<img src="${esc(p.file_url)}" alt="" style="display:block;max-width:220px;max-height:160px;object-fit:cover;border-radius:4px"/>` +
      `</button>`
    : "";
  return (
    `<div style="min-width:200px;font-size:12px">${img}` +
    `<div style="font-weight:600;word-break:break-all">${esc(p.original_name)}</div>` +
    `<div style="color:#6b7280">${esc(when)}${p.camera ? ` · ${esc(p.camera)}` : ""}</div>` +
    `<div style="margin-top:4px">${near}</div>` +
    `<div style="margin-top:4px">${esc(p.line_item_name)} · <a href="/orders/${p.order_id}" target="_blank" rel="noreferrer">${esc(p.order_number)}</a>` +
    ` · ${status}${p.photo_check === "suspect" ? " · do weryfikacji" : ""}${p.photo_check === "screenshot" ? " · zrzut ekranu" : ""}</div>` +
    `</div>`
  );
}

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};
// Owner names/shares are free-text → escape before injecting into the Leaflet popup HTML.
const esc = (s: string | null | undefined): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => HTML_ESCAPE[c]);

// Matches the OutcomeBadge palette (ui.tsx) so the popup reads like the SPRAWY list.
const PROC_OUTCOME_COLOR: Record<string, string> = {
  positive: "#16a34a",
  negative: "#dc2626",
  partial: "#d97706",
  none: "#6b7280",
};

/** Latest date our wniosek in the thread actually went out (SUBMITTED / COMPLETED applications).
 *  `date_sent` is the user-entered dispatch date; `submitted_at` the submit timestamp —
 *  the fallback covers items submitted without an explicit dispatch date. */
function lastApplicationSentDate(pr: Proceeding): string | null {
  let best: string | null = null;
  for (const it of pr.items) {
    if (it.kind !== "application" || (it.app_status !== "submitted" && it.app_status !== "completed")) continue;
    const d = it.date_sent ?? it.submitted_at;
    if (d && (!best || d > best)) best = d;
  }
  return best;
}

/** Rich parcel popup: TERYT, status (PL) and a table of owners with their per-owner
 *  access status + the negotiator's WPW status. Returns an HTML string for Leaflet's bindPopup.
 *  `wpwByOwner` maps owner-id → that owner's current WPW (currentWpwByOwner: the latest
 *  declaration across the owner's WPW orders), which leads the „Dostęp" cell until GK
 *  accepts the consent into the title;
 *  `proceedings` are the sprawy touching this parcel (directly or via its owners). */
function parcelPopupHtml(
  p: Parcel,
  wpwByOwner: Map<string, WpwHandover>,
  proceedings: Proceeding[],
  projectId: string,
): string {
  const statusLabel =
    p.status_display || PARCEL_STATUS_LABEL[p.status] || p.status;
  const statusColor = parcelDotColor(p.status);
  const dot = (color: string, size: number) =>
    `<span style="display:inline-block;width:${size}px;height:${size}px;border-radius:9999px;background:${color};margin-right:5px;vertical-align:middle"></span>`;

  // Grupa własności (kod własności = EGiB grupa rejestrowa) — labelled/coloured with the
  // same lookup the ZE tender map uses, so the popup reads identically across both maps.
  const ownershipOpt = findOwnershipOption(p.ownership_code);
  const ownershipLabel = ownershipOpt?.label || p.ownership_code || "—";
  const ownershipColor = ownershipCodeColor(p.ownership_code);

  // Per-owner „Dostęp": the formal title, or — until GK accepts the consent and the title
  // catches up — the status of the owner's current WPW order (wpwOwnerStatus.ts).
  const cells = new Map(p.owners.map((o) => [o.id, ownerAccessCell(o, wpwByOwner.get(o.id))]));
  const awaitingGk = p.access_not_required
    ? 0
    : [...cells.values()].filter((c) => c.awaitingGk).length;
  const small = (color: string, html: string) =>
    `<div style="color:${color};font-size:10px;overflow-wrap:anywhere">${html}</div>`;

  // Owners largest-share-first; spouses whose wypis states a marital-community share
  // (ParcelOwner.marital_group) stay adjacent and show the joint share once, in a rowspan
  // cell marked "małżeństwo".
  const ownerRows = groupOwnersForDisplay(p.owners)
    .map((g) =>
      g.owners
        .map((o, i) => {
          const cell = cells.get(o.id)!;
          const h = cell.handover;
          const when = h?.declared_status_at
            ? ` <span style="color:#6b7280;white-space:nowrap">· ${fmtDate(h.declared_status_at)}</span>`
            : "";
          const stale = cell.stale
            ? ' <span style="color:#d97706">(nieakt. wersja)</span>'
            : "";
          // WPW-led: which order the status comes from, where the claim stands with GK and
          // the formal title when it says more than „Nie rozpoczęto". Title-led: the WPW
          // status as a second line.
          const detail = cell.fromWpw && h
            ? small("#6b7280", h.order ? `WPW · ${esc(h.order.order_number ?? "zamówienie")}` : "WPW poza zamówieniem") +
              (cell.gkNote ? small(cell.gkNote.color, esc(cell.gkNote.text)) : "") +
              (cell.supersededNote ? small("#d97706", esc(cell.supersededNote)) : "") +
              (cell.titleNote ? small("#6b7280", esc(cell.titleNote)) : "")
            : h
              ? `<div style="color:${WPW_DECLARED_STATUS_COLOR[h.declared_status]}">WPW: ${esc(WPW_DECLARED_STATUS_LABEL[h.declared_status])}${when}</div>`
              : "";
          const nameStyle = g.married ? ";border-left:3px solid #fb7185;padding-left:5px" : "";
          const shareCell = g.married
            ? i === 0
              ? `<td rowspan="${g.owners.length}" style="padding:2px 8px;text-align:right;white-space:nowrap;vertical-align:middle" title="Wspólność małżeńska według wypisu — wspólny udział małżonków">
                  <span style="color:#be123c;font-weight:600">${esc(o.ownership_share) || "—"}</span>
                  <div style="font-size:9px;color:#be123c;text-transform:uppercase;letter-spacing:0.04em">małżeństwo</div>
                </td>`
              : ""
            : `<td style="padding:2px 8px;color:#6b7280;text-align:right;white-space:nowrap">${esc(o.ownership_share) || "—"}</td>`;
          // Udział trzymany samodzielnie OBOK wspólności — przy nazwisku, bo komórka
          // udziału grupy (rowspan) niesie udział wspólności.
          const ownShare = o.own_share
            ? ` <span style="color:#6b7280;white-space:nowrap" title="Udział trzymany samodzielnie, obok udziału we wspólności małżeńskiej">(${esc(o.own_share)} samodzielnie)</span>`
            : "";
          return `<tr>
        <td style="padding:2px 8px 2px 0${nameStyle}">${esc(o.full_name) || "—"}${ownShare}</td>
        ${shareCell}
        <td style="padding:2px 0 2px 8px">${dot(cell.color, 7)}${esc(cell.label)}${cell.fromWpw ? when : stale}${detail}</td>
      </tr>`;
        })
        .join(""),
    )
    .join("");

  // Sprawy touching this parcel — closed ones show the outcome + the closing note
  // (e.g. warunki attached to a positive decision), readable straight from the map.
  const procRows = proceedings
    .map((pr) => {
      const isClosed = pr.status === "closed";
      const color = isClosed
        ? (PROC_OUTCOME_COLOR[pr.outcome] ?? "#6b7280")
        : "#2563eb";
      const statusTxt = isClosed
        ? `${esc(pr.outcome_display)}${pr.closed_at ? ` · ${fmtDate(pr.closed_at)}` : ""}`
        : "otwarte";
      const note =
        isClosed && pr.closing_note
          ? `<div style="color:#374151;font-style:italic;white-space:pre-wrap;margin:1px 0 3px 12px">${esc(pr.closing_note)}</div>`
          : "";
      // "Kiedy poszedł wniosek" — the dispatch date of the latest submitted application,
      // plus the running KPA deadline for open threads (red once overdue).
      const sent = lastApplicationSentDate(pr);
      const kpa =
        !isClosed && pr.kpa_deadline
          ? ` · <span style="color:${pr.kpa_overdue ? "#dc2626" : "#6b7280"}">termin KPA ${fmtDate(pr.kpa_deadline)}</span>`
          : "";
      const sentLine = sent
        ? `<div style="color:#6b7280;margin-left:12px">wniosek wysłany ${fmtDate(sent)}${kpa}</div>`
        : "";
      return `<div style="margin-top:3px">${dot(color, 7)}${esc(pr.type_name)}${
        pr.type_note ? ` <span style="color:#6b7280">— ${esc(pr.type_note)}</span>` : ""
      }<span style="color:${color};font-weight:600"> · ${statusTxt}</span></div>${sentLine}${note}`;
    })
    .join("");
  const procSection = procRows
    ? `<div style="margin-top:6px;border-top:1px solid #e5e7eb;padding-top:5px">
        <div style="color:#9ca3af;font-weight:600">Sprawy / decyzje</div>${procRows}
      </div>`
    : "";

  const hasWpw = p.owners.some((o) => wpwByOwner.has(o.id));
  const wpwButton = hasWpw
    ? `<button data-wpw-parcel="${esc(p.id)}" data-wpw-label="WPW — ${esc(p.teryt)}" style="margin-top:6px;cursor:pointer;font-size:11px;border:1px solid #ccc;background:#fff;padding:3px 8px;font-family:monospace">Pokaż szczegóły zamówień WPW</button>`
    : "";

  const owners = p.access_not_required
    ? `<div style="color:#6b7280;margin-top:5px">Dostęp niewymagany.</div>`
    : p.owners.length === 0
      ? `<div style="color:#6b7280;margin-top:5px">Brak właścicieli — uzupełnij wypis na zakładce „Działki".</div>`
      : `<table style="border-collapse:collapse;margin-top:6px;font-size:11px">
          <thead><tr style="color:#9ca3af;text-align:left">
            <th style="font-weight:600;padding:0 8px 3px 0">Właściciel</th>
            <th style="font-weight:600;padding:0 8px 3px 8px;text-align:right">Udział</th>
            <th style="font-weight:600;padding:0 0 3px 8px">Dostęp</th>
          </tr></thead>
          <tbody>${ownerRows}</tbody>
        </table>`;

  return `<div style="font-size:12px;line-height:1.45;min-width:200px;max-width:300px;color:#111">
    <a href="/power-projects/${esc(projectId)}?tab=parcels&amp;parcel=${esc(p.id)}" target="_blank" rel="noopener"
       title="Otwórz tę działkę w zakładce „Działki” (nowa karta)"
       style="font-weight:700;font-family:monospace;color:#dc1f1f;text-decoration:none;display:inline-flex;align-items:center;gap:5px">${esc(p.teryt)}<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"></path><path d="M10 14 21 3"></path><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path></svg></a>
    <div style="margin-top:3px">${dot(statusColor, 8)}${esc(statusLabel)}</div>
    ${p.is_used ? "" : `<div style="color:#92400e;margin-top:1px" title="Trasa nie przebiega przez działkę i nie stoi na niej żaden obiekt — biegnie wzdłuż jej granicy, ale po stronie sąsiada, albo działka została zamówiona w wypisie „na zapas”">${dot(PARCEL_SPARE_FILL, 8)}Działka na zapas (niewykorzystywana)</div>`}
    <div style="color:#6b7280;margin-top:1px">${dot(ownershipColor, 8)}Grupa własności: <b style="color:#111">${esc(ownershipLabel)}</b></div>
    <div style="color:#6b7280;margin-top:1px">Uzgodnieni właściciele: <b style="color:#111">${p.owners_agreed}/${p.owners_total}</b>${
      awaitingGk
        ? ` <span style="color:#d97706" title="Zgody zebrane w zamówieniu WPW, jeszcze niezatwierdzone przez GK — nie liczą się do uzgodnionych">· zgoda WPW czeka na GK: ${awaitingGk}</span>`
        : ""
    }</div>
    ${p.is_connected_entity ? `<div style="color:#7c3aed;font-weight:600;margin-top:1px">Działka podmiotu przyłączanego</div>` : ""}
    ${owners}
    ${procSection}
    ${wpwButton}
  </div>`;
}

/** Short ownership-group identifier drawn permanently on the parcel (the "numer grupy
 *  własności" the popup shows in full as "N — Name"). Numeric EGiB register-group codes
 *  (1, 2, 4, 7, 11, 13, 15…) are shown as the bare number; manual override codes have no
 *  register number, so their short label is used instead. Empty code → no label. */
function parcelGroupLabel(code: string | null | undefined): string {
  const c = (code ?? "").trim();
  if (!c) return "";
  if (/^\d+$/.test(c)) return c;
  return findOwnershipOption(c)?.label ?? c;
}

// Non-interactive divIcon for the on-parcel ownership-group number. iconSize [0,0] + a CSS
// translate(-50%,-50%) on the badge centres it on the marker's latlng, so nudging the label
// away from an overlapping object is a plain `marker.setLatLng(...)` and it stays centred.
// `text` must already be HTML-escaped.
function parcelGroupIcon(text: string): L.DivIcon {
  return L.divIcon({
    html: `<span class="pp-parcel-group-badge">${text}</span>`,
    className: "pp-parcel-group-label-wrap",
    iconSize: [0, 0],
  });
}

// Half-extent (px) of a rendered number badge — rough, from text length; used only for the
// box-overlap test against object markers, so an estimate is fine.
function labelHalfExtent(text: string): { hw: number; hh: number } {
  return { hw: text.length * 3.6 + 9, hh: 9 };
}

// Rings ([x,y] planar metres) of the biggest polygon in a Polygon/MultiPolygon parcel
// geometry, projected via the map CRS so the pole-of-inaccessibility maths sees a metric,
// undistorted space. `ringArea` (on raw lng/lat) only ranks the parts, so its unit is moot.
// Returns null for missing / non-polygon geometry.
function projectedParcelRings(
  geometry: { type: string; coordinates: unknown },
  crs: L.CRS,
): Rings | null {
  const c = geometry.coordinates;
  if (!Array.isArray(c) || c.length === 0) return null;
  let lnglat: [number, number][][] | null = null;
  if (geometry.type === "Polygon") {
    lnglat = c as [number, number][][];
  } else if (geometry.type === "MultiPolygon") {
    let bestArea = -Infinity;
    for (const poly of c as [number, number][][][]) {
      if (!poly?.[0]?.length) continue;
      const area = Math.abs(ringArea(poly[0]));
      if (area > bestArea) {
        bestArea = area;
        lnglat = poly;
      }
    }
  }
  if (!lnglat?.[0]?.length) return null;
  return lnglat.map((ring) =>
    ring.map(([lng, lat]) => {
      const pt = crs.project(L.latLng(lat, lng));
      return [pt.x, pt.y] as [number, number];
    }),
  );
}

// One on-map parcel number: its marker, the ordered interior anchor points it may occupy
// (pole of inaccessibility first, then fallbacks — all guaranteed on the parcel), and its
// pixel half-size for the overlap test.
interface ParcelLabelEntry {
  marker: L.Marker;
  candidates: L.LatLng[];
  hw: number;
  hh: number;
}

function fmtDistance(m: number): string {
  return m >= 1000
    ? `${(m / 1000).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} km`
    : `${Math.round(m).toLocaleString("pl-PL")} m`;
}

function fmtAreaM2(m2: number | null | undefined): string | null {
  if (m2 == null) return null;
  return m2 >= 10_000
    ? `${(m2 / 10_000).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} ha`
    : `${Math.round(m2).toLocaleString("pl-PL")} m²`;
}

// A failed background task stores its full Python traceback in `error` (NOT in `log`), so the
// live-log pane alone hides the real cause and the card would only say "sprawdź log poniżej".
// Surface the exception's final line — the human-meaningful part, e.g. "IntegrityError:
// duplicate key value …" — so a failed przeliczenie działek reads as a message, not a mystery.
function taskErrorLine(error: string | null | undefined): string {
  return (error ?? "").trim().split("\n").filter((l) => l.trim()).pop() ?? "";
}

// Compass bearing from → to (0° = north, clockwise). Used by the Street View tool to
// aim the panorama in the direction of the user's second click.
function headingBetween(from: L.LatLng, to: L.LatLng): number {
  const dLon = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Geoportal "informator" popup: the działka under a clicked point, independent of our own
 *  działki. Prefers the full KIEG attribute set (grupa rejestrowa, oznaczenie użytku/konturu,
 *  pole pow., data publikacji — exactly what the geoportal informator shows); falls back to
 *  the ULDK admin fields when KIEG is unavailable. Inline styles only (popup = outside React). */
// Stable id for the "Dodaj działkę do systemu" button inside the informator popup — only
// one informator popup is open at a time, so the handler binds to it by id after setContent.
const ADD_PARCEL_BTN_ID = "pp-add-parcel-btn";

function uldkPopupHtml(info: UldkPointInfo, latlng: L.LatLng, alreadyInSystem: boolean): string {
  const row = (label: string, val: string | null) =>
    val
      ? `<tr>
          <td style="color:#6b7280;padding:2px 10px 2px 0;font-size:10px;vertical-align:top">${esc(label)}</td>
          <td style="text-align:right;padding:2px 0;font-size:11px;vertical-align:top">${esc(val)}</td>
        </tr>`
      : "";
  const rows =
    info.attributes && info.attributes.length
      ? info.attributes.map((a) => row(a.label, a.value)).join("")
      : [
          row("Powierzchnia", fmtAreaM2(info.area_m2)),
          row("Obręb", info.region || null),
          row("Gmina", info.commune || null),
          row("Powiat", info.county || null),
          row("Województwo", info.voivodeship || null),
        ].join("");
  // Quick jumps for the identified działka — plain links (open in a new tab), no JS wiring.
  // Street View at the clicked point; Geoportal via GUGiK's own by-TERYT deep link into the
  // new viewer (see geoportalParcelUrl), so it lands on this exact parcel.
  const linkStyle =
    "flex:1;text-align:center;text-decoration:none;font-size:11px;border:1px solid #d1d5db;background:#fff;color:#111;padding:4px 8px;border-radius:2px";
  const svUrl = `https://maps.google.com/maps?layer=c&cbll=${latlng.lat},${latlng.lng}&cbp=11,0,0,0,0`;
  const gpUrl = info.teryt ? geoportalParcelUrl(info.teryt) : null;
  const actions = `<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e5e7eb;display:flex;gap:6px">
      <a href="${svUrl}" target="_blank" rel="noopener noreferrer" style="${linkStyle}">Street View</a>
      ${gpUrl ? `<a href="${gpUrl}" target="_blank" rel="noopener noreferrer" style="${linkStyle}">Geoportal</a>` : ""}
    </div>`;
  // "Dodaj działkę do systemu" — creates the ProjectParcel for this connection from the
  // identified TERYT and pulls its official geometry. Rendered only when the parcel was
  // identified (has a TERYT); a chip is shown instead when it is already in the project.
  const addRow = !info.teryt
    ? ""
    : alreadyInSystem
      ? `<div style="margin-top:6px;text-align:center;font-size:11px;color:#059669;font-weight:600">✓ Działka jest już w systemie</div>`
      : `<button id="${ADD_PARCEL_BTN_ID}" type="button" style="margin-top:6px;width:100%;cursor:pointer;font-size:11px;font-weight:600;border:1px solid #7c3aed;background:#7c3aed;color:#fff;padding:6px 8px;border-radius:2px">Dodaj działkę do systemu</button>`;
  return `<div style="min-width:210px;max-width:320px;font-family:ui-monospace,monospace;line-height:1.4">
      <div style="font-weight:700;font-size:13px;color:#111;padding-bottom:5px;margin-bottom:5px;border-bottom:2px solid #e5e7eb">${esc(info.teryt ?? "—")}</div>
      <div style="font-size:10px;color:#9ca3af;margin-bottom:4px">Geoportal (ewidencja gruntów) — niezależnie od działek projektu</div>
      <table style="width:100%;border-collapse:collapse">
        ${rows}
      </table>
      ${actions}
      ${addRow}
    </div>`;
}

// Kreskowe nakładki na działki. Leaflet nie ma API wzorów wypełnienia, więc raz
// wstrzykujemy SVG <pattern> do <svg> warstwy overlayPane i kierujemy na niego fill
// ścieżki działki przez url(#…).
//   • PRIVATE  — czarna kreska 45°, grunt prywatny (osoba fizyczna / grupa 7, 15),
//   • CONNECTED — fioletowa kreska przerywana w PRZECIWNYM kierunku (−45°), działka
//     podmiotu przyłączanego. Odwrotny kąt + kreska przerywana czytają się osobno,
//     także gdy działka jest jednocześnie prywatna (oba wzory nakładają się w krzyż).
type HatchSpec = {
  id: string;
  rotate: number;
  spacing: number;
  stroke: string;
  width: number;
  opacity: number;
  dash?: string;
};

const PRIVATE_HATCH: HatchSpec = {
  id: "pp-private-parcel-hatch",
  rotate: 45,
  spacing: 7,
  stroke: "#111",
  width: 1.4,
  opacity: 0.75,
};

const CONNECTED_HATCH: HatchSpec = {
  id: "pp-connected-entity-hatch",
  rotate: -45,
  spacing: 9,
  stroke: "#7c3aed",
  width: 2,
  opacity: 0.85,
  dash: "5 3",
};

// Powierzchnia do wyłączenia z produkcji rolniczej — ten sam prostokąt, który rysuje
// skrypt CAD i z którego bierze się liczba we wniosku. Czerwień i gęste kreskowanie,
// bo to jedyna warstwa mówiąca „za ten kawałek gruntu zapłacimy należność".
const ODROLNIENIE_HATCH: HatchSpec = {
  id: "pp-odrolnienie-hatch",
  rotate: 45,
  spacing: 5,
  stroke: "#c31f1f",
  width: 1.6,
  opacity: 0.9,
};

// Prywatna = strona własności z KATEGORII UZGODNIENIA policzonej przez backend
// (utils.isPrivateSideParcel — lustro services.parcel_inclusion_kind), a przy gruncie w
// użytkowaniu wieczystym także wtedy, gdy użytkownik wieczysty jest podmiotem bez urzędu
// (jego zgoda idzie drogą WPW, choć właścicielem gruntu jest gmina / powiat). Bez kategorii
// zostaje heurystyka: właściciel-osoba fizyczna albo kod własności 7/15. JEDNO źródło dla
// kreskowania na mapie i dla wiersza w legendzie, żeby próbka nigdy nie kłamała o tym, co
// mapa faktycznie zakreskowała.
function isPrivateParcel(p: Parcel): boolean {
  return isPrivateSideParcel(p);
}

/** Próbka wypełnienia działki do legendy — obrys rysowany dokładnie tak, jak drawParcels
 *  styluje wielokąt (ciągły grafit dla wykorzystywanej, przerywany dla działki „na zapas"). */
function ParcelFillSwatch({ fill, dashed }: { fill: string; dashed?: boolean }) {
  return (
    <svg width="20" height="12" aria-hidden>
      <rect
        x="0.5" y="0.5" width="19" height="11"
        fill={fill} fillOpacity="0.7"
        stroke={dashed ? "#78716c" : "#555"}
        strokeDasharray={dashed ? "4 3" : undefined}
      />
    </svg>
  );
}

/** Próbka kreskowania do legendy — ten sam wzór (kąt, kolor, grubość, kreska), którym
 *  ensureHatchPattern maluje działkę na mapie, tylko w małym SVG-u. Odstęp ścieśniamy do
 *  6 px: przy oryginalnym (7–9 px) w kratkę 20×12 trafiała jedna kreska, a kreskowanie
 *  przerywane podmiotu przyłączanego gubiło się zupełnie. */
function HatchSwatch({ spec }: { spec: HatchSpec }) {
  const patternId = `${spec.id}-swatch`;
  const spacing = Math.min(spec.spacing, 6);
  return (
    <svg width="20" height="12" aria-hidden>
      <defs>
        <pattern
          id={patternId}
          patternUnits="userSpaceOnUse"
          width={spacing}
          height={spacing}
          patternTransform={`rotate(${spec.rotate})`}
        >
          <line
            x1="0" y1="0" x2="0" y2={spacing}
            stroke={spec.stroke} strokeWidth={spec.width} strokeOpacity={spec.opacity}
            strokeDasharray={spec.dash}
          />
        </pattern>
      </defs>
      <rect x="0.5" y="0.5" width="19" height="11" fill="#fff" stroke="#9ca3af" />
      <rect x="0.5" y="0.5" width="19" height="11" fill={`url(#${patternId})`} stroke="none" />
    </svg>
  );
}

function ensureHatchPattern(map: L.Map, spec: HatchSpec) {
  const svg = map.getPane("overlayPane")?.querySelector("svg");
  if (!svg || svg.querySelector(`#${spec.id}`)) return;
  const NS = "http://www.w3.org/2000/svg";
  let defs = svg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS(NS, "defs");
    svg.insertBefore(defs, svg.firstChild);
  }
  const pattern = document.createElementNS(NS, "pattern");
  pattern.setAttribute("id", spec.id);
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  pattern.setAttribute("width", String(spec.spacing));
  pattern.setAttribute("height", String(spec.spacing));
  pattern.setAttribute("patternTransform", `rotate(${spec.rotate})`);
  const line = document.createElementNS(NS, "line");
  line.setAttribute("x1", "0");
  line.setAttribute("y1", "0");
  line.setAttribute("x2", "0");
  line.setAttribute("y2", String(spec.spacing));
  line.setAttribute("stroke", spec.stroke);
  line.setAttribute("stroke-width", String(spec.width));
  line.setAttribute("stroke-opacity", String(spec.opacity));
  if (spec.dash) line.setAttribute("stroke-dasharray", spec.dash);
  pattern.appendChild(line);
  defs.appendChild(pattern);
}

/** Tryb „projekt prowadzony przez firmę" (warsztat ZE): wgrywanie trasy jak u firmy
 *  (RouteUploadPanel, bez wariantów i wersji), sekcje MDCP i mapy zasadniczej POD mapą,
 *  bez sekcji formatek; obrysy i punkty MDCP widzi tylko nasza strona. */
export interface LedMapMode {
  leadershipId: string;
  hasRoute: boolean;
  dxf: LedFile | null;
  tutorialHref: string;
}

export function MapTab({ connection, led }: { connection: PowerProject; led?: LedMapMode }) {
  const { data: variants, isLoading } = useVariants(connection.id);
  const { data: parcels } = useParcels(connection.id);
  const createVariant = useCreateVariant(connection.id);
  const deleteVariant = useDeleteVariant(connection.id);
  const setBindingVariant = useSetBindingVariant(connection.id);
  const upload = useUploadRouteVersion(connection.id);

  // WPW handovers for the project — surfaced in the parcel popup (status per owner) and behind a
  // "Pokaż rozmowę WPW" button that opens the conversation modal over the map.
  const { data: handovers } = useWpwHandovers({ connection: connection.id });
  const wpwByOwner = useMemo(() => currentWpwByOwner(handovers ?? []), [handovers]);
  // Sprawy per parcel for the popup — matched directly (TECHNICAL threads scope by parcel)
  // or through the parcel's owners (OWNER/OFFICE threads scope by owner), so a decyzja's
  // outcome + closing note shows up on every działka it concerns.
  const { data: mapProceedings } = useProceedings({ connection: connection.id });
  const proceedingsByParcel = useMemo(() => {
    const byParcel = new Map<string, Proceeding[]>();
    if (!mapProceedings?.length || !parcels?.length) return byParcel;
    const ownerToParcels = new Map<string, string[]>();
    for (const p of parcels)
      for (const o of p.owners) {
        const list = ownerToParcels.get(o.id) ?? [];
        list.push(p.id);
        ownerToParcels.set(o.id, list);
      }
    for (const pr of mapProceedings) {
      const ids = new Set<string>(pr.parcel_ids);
      for (const oid of pr.owner_ids)
        for (const pid of ownerToParcels.get(oid) ?? []) ids.add(pid);
      for (const pid of ids) {
        const list = byParcel.get(pid) ?? [];
        list.push(pr);
        byParcel.set(pid, list);
      }
    }
    return byParcel;
  }, [mapProceedings, parcels]);

  const [wpwModal, setWpwModal] = useState<{
    parcelId: string;
    label: string;
  } | null>(null);
  // Stable bridge from the Leaflet popup button (DOM) to React state, avoiding stale closures.
  const openWpwRef = useRef<(parcelId: string, label: string) => void>(
    () => {},
  );
  openWpwRef.current = (parcelId, label) => setWpwModal({ parcelId, label });

  // Parcel whose Leaflet popup is currently open. While set, the right-hand column
  // swaps its usual cards for that parcel's event-history table; closing the popup
  // (clicking elsewhere / the X) restores the normal panel. Follows popupopen/popupclose
  // so the panel and the popup can never disagree about what is selected.
  const [historyParcelId, setHistoryParcelId] = useState<string | null>(null);
  const historyParcel = useMemo(
    () =>
      historyParcelId
        ? ((parcels ?? []).find((p) => p.id === historyParcelId) ?? null)
        : null,
    [parcels, historyParcelId],
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const [editVersionOpen, setEditVersionOpen] = useState(false);
  // Route version staged for deletion — drives the "UWAGA" confirmation modal, opened
  // from the timeline trash icon or the "Usuń wersję" button in the "Wersja trasy" card.
  const [versionToDelete, setVersionToDelete] = useState<TimelineVersion | null>(null);
  const allVersions = useMemo(
    () =>
      (variants ?? []).flatMap((v) =>
        v.versions.map((ver) => ({
          ...ver,
          variantLabel: v.label,
          variantBinding: v.is_binding,
          variantCanDelete: v.can_delete,
        })),
      ),
    [variants],
  );
  // Wiele wariantów, żaden nieobowiązujący → „aktualnych" jest tyle, ile wariantów;
  // dopiero wybór obowiązującego mówi, z której wersji liczą się statusy/sprawy.
  const multiVariant = (variants?.length ?? 0) > 1;
  const anyBinding = (variants ?? []).some((v) => v.is_binding);
  const bindingVariantId = (variants ?? []).find((v) => v.is_binding)?.id ?? "";
  const canManageRoutes = (variants ?? []).some((v) => v.can_delete);
  const selectedVersion =
    allVersions.find((v) => v.id === selectedVersionId) ||
    currentVersionOf(variants);
  // The selected version with its variant facts — the fallback above is a bare
  // RouteVersion, so look it up again for the delete control.
  const selectedTimelineVersion = selectedVersion
    ? (allVersions.find((v) => v.id === selectedVersion.id) ?? null)
    : null;
  // Newest sibling of the version staged for deletion: it takes over as the variant's
  // current version. None left = the variant goes with the version (the modal warns).
  const versionToDeleteSuccessor = useMemo(() => {
    if (!versionToDelete) return null;
    return allVersions
      .filter((v) => v.variant === versionToDelete.variant && v.id !== versionToDelete.id)
      .reduce<TimelineVersion | null>(
        (best, v) => (best == null || v.number > best.number ? v : best),
        null,
      );
  }, [versionToDelete, allVersions]);

  // Two map modes:
  //   "curated" = our styled vector overlays (parcels by status + route), client-side;
  //   "raw"     = the FULL DXF exactly as uploaded, served as a WMS from a PyQGIS-built
  //               QGZ (qgis_builder) — real OGR layers + styling, the QGIS-native way.
  const [mode, setModeState] = useState<MapMode>(
    () => loadMapMode(connection.id) ?? "curated",
  );
  const setMode = (next: MapMode) => {
    setModeState(next);
    saveMapMode(connection.id, next);
  };
  const [wmsLoading, setWmsLoading] = useState(false);
  // Raw-mode overlay toggles — off by default ("surowy" = the DXF as-is); OSM base on.
  // Remembered per browser: they are display preferences, and the tab switch that remounts
  // this map must not make the user re-tick them (same reason as the GUGiK panel).
  const [rawShowParcels, setRawShowParcels] = usePersistentToggle(
    "powerProjects.map.rawShowParcels",
    false,
  );
  const [rawShowObjects, setRawShowObjects] = usePersistentToggle(
    "powerProjects.map.rawShowObjects",
    false,
  );
  const [rawShowOsm, setRawShowOsm] = usePersistentToggle("powerProjects.map.rawShowOsm", true);
  // GUGiK reference overlays — independent of curated/raw, off by default. One toggle for
  // działki (granice + numery together), one for GESUT infrastructure. Remembered per browser
  // (useGugikState): the tab switch that remounts this map must not clear what was ticked.
  const [gugik, setGugik] = useGugikState("powerProjects.map", {
    parcels: false,
    infra: false,
    ortho: false,
    mpzp: false,
    gdos: false,
    konserwator: false,
    konserwatorEwidencja: false,
    gddkia: false,
    closedAreas: false,
  });
  // Current map zoom, tracked only to warn that GUGiK tiles need a close zoom to render.
  const [zoom, setZoom] = useState(6);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { data: wms } = useSignedWmsUrl(
    selectedVersion?.gis_project_id || undefined,
  );
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  // A background task is in flight from the moment we have its id until it resolves — true
  // even before the first poll returns (task.data still undefined). Import/copy is async, so
  // the button must stay busy the WHOLE time, not just during the POST that starts it.
  const taskBusy =
    !!taskId && (task.data == null || ["pending", "running"].includes(task.data.status));
  const qc = useQueryClient();
  // The "Przetwarzanie" live-log card sits at the bottom of the side column — off-screen
  // when a task is started from a card higher up (e.g. "Przebuduj podgląd (QGIS)" in
  // "Wersja trasy"). Scroll it into view whenever a new task starts, so the live log is
  // actually visible instead of silently appearing below the fold.
  const taskLogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!taskId) return;
    // Defer one frame: the card mounts on the same render that sets taskId.
    const raf = requestAnimationFrame(() =>
      taskLogRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
    );
    return () => cancelAnimationFrame(raf);
  }, [taskId]);

  // Działki whose stored geometry landed outside Poland (legacy SRID mistag). Surfaced as a
  // one-click re-fetch next to "Przelicz działki (ULDK)"; hidden when nothing is offshore.
  const offshoreCount = useMemo(
    () =>
      (parcels ?? []).filter(
        (p) => p.geometry && isProjectParcel(p) && geometryOutsidePoland(p.geometry),
      ).length,
    [parcels],
  );
  const [refetchingOcean, setRefetchingOcean] = useState(false);
  const doRefetchOcean = async () => {
    setRefetchingOcean(true);
    try {
      const r = await refetchOceanParcels(connection.id);
      setTaskId(r.task_id);
    } finally {
      setRefetchingOcean(false);
    }
  };

  // When the pipeline finishes, refresh parcels (map) + versions so the result appears
  // without the user navigating or guessing whether anything is happening. On an upload
  // the task returns the new version id — auto-select it so the map jumps to the result.
  // Refresh on failure too: an upload whose map didn't build still SAVED the version and
  // its działki (the task carries them in its result), so hiding them would be a second,
  // invented problem on top of the missing preview.
  // Nakładanie działek po „Przelicz działki (ULDK)": zadanie z check_overlaps oddaje w
  // wyniku grupy działek z ULDK, które nakładają się na istniejące (podział/scalenie w
  // ewidencji) — nie dodaje ich samo. Okno zbiera jedną decyzję na grupę i wysyła je do
  // resolve-parcel-conflicts. Otwierane raz na zadanie (conflictTaskId), żeby ponowny
  // render po „Później" nie otwierał go z powrotem.
  const [conflictGroups, setConflictGroups] = useState<ParcelConflictGroup[]>([]);
  const [conflictTaskId, setConflictTaskId] = useState<string | null>(null);
  const [conflictVersionId, setConflictVersionId] = useState<string | null>(null);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [conflictsSubmitting, setConflictsSubmitting] = useState(false);
  const [conflictsError, setConflictsError] = useState<string | null>(null);
  // Pakiety WPW (i listy automatyczne), którym nowa trasa zdezaktualizowała mapę — komunikat
  // do potwierdzenia po zakończeniu zadania, raz na zadanie (wpwStaleTaskId).
  const [wpwStale, setWpwStale] = useState<WpwRouteStalePackage[]>([]);
  const [wpwStaleHeld, setWpwStaleHeld] = useState(0);
  const [wpwStaleTaskId, setWpwStaleTaskId] = useState<string | null>(null);
  const [wpwStaleOpen, setWpwStaleOpen] = useState(false);

  const taskStatus = task.data?.status;
  useEffect(() => {
    if (taskStatus === "success" || taskStatus === "failure") {
      qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
      qc.invalidateQueries({ queryKey: ["pp", "variants", connection.id] });
      qc.invalidateQueries({ queryKey: ["pp", "wpw-mail-letters"] });
      const result = task.data?.result as
        | {
            version_id?: string; route_version?: string; parcel_conflicts?: ParcelConflictGroup[];
            wpw_stale?: WpwRouteStalePackage[]; wpw_letters_held?: number;
          }
        | null;
      const versionId = result?.version_id;
      if (versionId) setSelectedVersionId(versionId);
      const conflicts = result?.parcel_conflicts;
      if (conflicts && conflicts.length > 0 && taskId && taskId !== conflictTaskId) {
        setConflictTaskId(taskId);
        setConflictVersionId(result?.route_version ?? null);
        setConflictGroups(conflicts);
        setConflictsError(null);
        setConflictsOpen(true);
      }
      const stale = result?.wpw_stale;
      if (stale && stale.length > 0 && taskId && taskId !== wpwStaleTaskId) {
        setWpwStaleTaskId(taskId);
        setWpwStale(stale);
        setWpwStaleHeld(result?.wpw_letters_held ?? 0);
        setWpwStaleOpen(true);
      }
    }
  }, [taskStatus, connection.id, qc, task.data?.result, taskId, conflictTaskId, wpwStaleTaskId]);

  const submitConflictDecisions = async (decisions: ParcelConflictDecision[]) => {
    const versionId = conflictVersionId ?? selectedVersion?.id;
    if (!versionId) return;
    setConflictsSubmitting(true);
    setConflictsError(null);
    try {
      await resolveParcelConflicts(versionId, decisions);
      setConflictsOpen(false);
      setConflictGroups([]);
      qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
      qc.invalidateQueries({ queryKey: ["pp", "variants", connection.id] });
    } catch (e) {
      setConflictsError(apiErr(e, "Nie udało się zastosować decyzji."));
    } finally {
      setConflictsSubmitting(false);
    }
  };

  const [variantId, setVariantId] = useState("");
  // The whole map (route + mapa zasadnicza) lives in a single DXF — no second file.
  const [dxfFile, setDxfFile] = useState<File | null>(null);
  const [layerPresetId, setLayerPresetId] = useState("");
  const [comment, setComment] = useState("");
  const [newVariantLabel, setNewVariantLabel] = useState("");
  // Route variant staged for deletion — drives the "UWAGA" confirmation modal.
  const [variantToDelete, setVariantToDelete] = useState<RouteVariant | null>(null);
  // Server-side refusal of that delete (403 for anyone but the project's koordynator,
  // dyrektor techniczny or CEO). Without this the modal just sat there on a refusal and
  // the button read as dead.
  const [variantDeleteError, setVariantDeleteError] = useState<string | null>(null);
  // Opt-in pre-upload check ("monit"): validate the DXF's layers against the preset
  // and let the user confirm/cancel in a modal BEFORE the upload pipeline starts.
  // Persisted per browser — designers who want the gate keep it across sessions.
  const [askBeforeUpload, toggleAskBeforeUpload] = usePersistentToggle(
    DXF_MONIT_STORAGE_KEY,
    false,
  );
  const [validation, setValidation] = useState<DxfValidationReport | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // "Wariant z przetargu ZE" — copy a drawing variant of the source tender as a NEW
  // project route variant (async task: copy + parcel discovery, same log as upload).
  const { data: tenderVariants } = useZEVariants(
    connection.source_tender ?? undefined,
  );
  const [importZeVariantId, setImportZeVariantId] = useState("");
  const [importLabel, setImportLabel] = useState("");
  // Optional target layer preset for the imported first version — lays it straight onto
  // the project's layers (Tauron/Energa) instead of tender-language ones. "" = tender layers.
  const [importPresetId, setImportPresetId] = useState("");
  const [importPending, setImportPending] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  // The panel stays expanded by default even once the project already has route
  // variants — importing another variant from the tender is a valid ongoing action,
  // so keep the controls visible (user can still collapse it manually via the chevron).
  const [zeImportExpandedOverride, setZeImportExpandedOverride] = useState<
    boolean | null
  >(null);
  const zeImportExpanded = zeImportExpandedOverride ?? true;

  const doImportFromTender = async () => {
    if (!importZeVariantId || importPending) return;
    setImportPending(true);
    setImportError(null);
    try {
      const r = await importTenderVariant(connection.id, {
        ze_variant: importZeVariantId,
        label: importLabel.trim() || undefined,
        preset: importPresetId || undefined,
      });
      setImportLabel("");
      setTaskId(r.task_id);
    } catch (e) {
      setImportError(
        (e as { response?: { data?: { errors?: { detail?: string } } } })
          ?.response?.data?.errors?.detail ||
          "Nie udało się skopiować mapy z przetargu.",
      );
    } finally {
      setImportPending(false);
    }
  };

  const { data: presets } = useDxfLayerPresets();
  const activePresets = (presets ?? []).filter((p) => p.is_active);
  const hasPresets = activePresets.length > 0;

  // ── typy obiektów ZE (język przetargu): stylowanie wykrytych linii/obiektów + legenda ──
  const { data: elementTypes } = useZEElementTypes();
  // Lookup by internal `code` AND by `preset_key` — DXF layer presets are edited by hand
  // and people naturally type the human slug (projektowana_ziemna_nn), not the internal
  // code (ziemna_nn_proj). Both must style identically.
  const etByCode = useMemo(() => {
    const m = new Map<string, ZEElementType>();
    for (const t of elementTypes ?? []) {
      m.set(t.code, t);
      if (t.preset_key) m.set(t.preset_key, t);
    }
    return m;
  }, [elementTypes]);
  // Legenda wykrytych typów na aktualnej wersji (linie z typed_lines + obiekty punktowe).
  // Every symbol drawn on the map gets a row: types with a ZEElementType row use the
  // tender swatch + label, the rest the same fallback styles the map paints them with
  // (red/blue-dashed lines, hash-derived object symbols) labelled by their slug.
  const detectedLegend = useMemo(() => {
    const counts = new Map<string, { kind: "line" | "object"; n: number }>();
    const bump = (code: string, kind: "line" | "object") => {
      const cur = counts.get(code);
      if (cur) cur.n += 1;
      else counts.set(code, { kind, n: 1 });
    };
    const typed = selectedVersion?.typed_lines?.features ?? [];
    for (const f of typed) bump(f.properties.semantic_type, "line");
    for (const o of selectedVersion?.objects ?? []) bump(o.semantic_type, "object");
    const rows = [...counts.entries()].map(([code, { kind, n }]) => {
      const et = etByCode.get(code);
      if (et) {
        return { key: code, swatch: elementSwatchSvg(et), label: et.label, n, order: et.order ?? 0 };
      }
      return {
        key: code,
        swatch: kind === "line" ? fallbackLineSwatch(code) : objectSymbolSvg(code),
        label: code.replace(/_/g, " ") || "obiekt",
        n,
        order: Number.MAX_SAFE_INTEGER,
      };
    });
    // Old versions without typed_lines draw the flat route/demolition geometries —
    // give those a legend row too (no count: they are merged geometries, not features).
    if (typed.length === 0 && selectedVersion?.geometry) {
      rows.push({ key: "__route", swatch: fallbackLineSwatch(null), label: "trasa", n: 0, order: -1 });
    }
    if (typed.length === 0 && selectedVersion?.demolition_geometry) {
      rows.push({ key: "__demolition", swatch: fallbackLineSwatch("likwidacja"), label: "demontaż / likwidacja", n: 0, order: -1 });
    }
    return rows.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, "pl"));
  }, [selectedVersion, etByCode]);
  // Legenda działek — kolory wypełnienia i kreskowanie, liczone z działek FAKTYCZNIE
  // rysowanych na mapie (tak jak legenda typów: tłumaczymy tylko to, co widać) i dokładnie
  // z reguł parcelFillKind: „na zapas" i „powiadomienie" mają własne wypełnienie NIEZALEŻNE
  // od statusu, więc dostają osobny wiersz zamiast koloru statusu. Wcześniej pod mapą
  // wyjaśniona była tylko działka „na zapas" i kreska podmiotu przyłączanego — szary
  // „Nie rozpoczęto", pozostałe kolory statusu i czarna kreska gruntu prywatnego nie.
  const parcelLegend = useMemo(() => {
    const drawn = (parcels ?? []).filter((p) => p.geometry && isProjectParcel(p));
    const byKey = new Map<ParcelFillKind, Parcel[]>();
    for (const p of drawn) {
      const key = parcelFillKind(p);
      const cur = byKey.get(key);
      if (cur) cur.push(p);
      else byKey.set(key, [p]);
    }
    const fills: { key: string; fill: string; dashed?: boolean; label: string; title?: string; n: number }[] = [];
    // Kolejność statusów bierzemy z klucza słownika etykiet (nie_rozpoczęto → problem),
    // żeby legenda czytała się tak samo jak filtr statusu na zakładce „Działki".
    for (const status of Object.keys(PARCEL_STATUS_LABEL) as ParcelStatus[]) {
      const group = byKey.get(status);
      if (!group) continue;
      fills.push({
        key: status,
        fill: parcelDotColor(status),
        // status_display z backendu (jak w popupie działki), z zapasem na słownik lokalny.
        label: group[0].status_display || PARCEL_STATUS_LABEL[status],
        n: group.length,
      });
    }
    const notification = byKey.get("notification");
    if (notification) {
      fills.push({
        key: "notification",
        fill: PARCEL_NOTIFICATION_FILL,
        label: "tylko zawiadomienie (demontaż)",
        title: "Tryb dostępu „Wymaga powiadomienia” — kolor niezależny od statusu działki",
        n: notification.length,
      });
    }
    const spare = byKey.get("spare");
    if (spare) {
      fills.push({
        key: "spare",
        fill: PARCEL_SPARE_FILL,
        dashed: true,
        label: "działka na zapas (trasa nie przebiega — nie wymaga uzgodnienia)",
        title:
          "Trasa nie przebiega przez działkę i nie stoi na niej żaden obiekt — biegnie wzdłuż jej granicy, ale po stronie sąsiada, albo działka została zamówiona w wypisie „na zapas”",
        n: spare.length,
      });
    }
    const hatches: { key: string; spec: HatchSpec; label: string; title: string; n: number }[] = [];
    const privateN = drawn.filter(isPrivateParcel).length;
    if (privateN) {
      hatches.push({
        key: "private",
        spec: PRIVATE_HATCH,
        label: "grunt prywatny",
        title:
          "Kategoria uzgodnienia prywatna (podmiot przyłączany / tranzyt) albo grunt w użytkowaniu wieczystym z użytkownikiem bez urzędu; bez kategorii: właściciel jest osobą fizyczną lub działka ma grupę rejestrową 7 / 15",
        n: privateN,
      });
    }
    const connectedN = drawn.filter((p) => p.is_connected_entity).length;
    if (connectedN) {
      hatches.push({
        key: "connected",
        spec: CONNECTED_HATCH,
        label: "działka podmiotu przyłączanego",
        title:
          "Flaga ustawiana na zakładce „Działki”. Wzór biegnie w przeciwną stronę niż kreska gruntu prywatnego, więc na prywatnej działce przyłączanego widać oba naraz",
        n: connectedN,
      });
    }
    return { fills, hatches };
  }, [parcels]);

  // Zwijana pod mapą — domyślnie schowana, żeby nie zabierać miejsca pod trasą.
  const [legendOpen, setLegendOpen] = useState(false);

  // ── MDCP: punkty zdjęciowe (punkt + azymut + numer) ──
  const { data: mdcpData } = useMdcpPanel(connection.id);
  // Memoised: both are dependencies of the overlay-drawing effect, and a fresh `[]` on
  // every render (query not loaded / layer disabled) would re-run it on ANY state change —
  // clearLayers() then closes the parcel popup the moment popupopen sets the history panel.
  const mdcpPoints = useMemo(() => mdcpData?.points ?? [], [mdcpData]);
  const mdcpBuffer = mdcpData?.buffer ?? null;
  // Pozostałe obrysy projektu (lista rozwijana panelu) — rysujemy je szarą kreską z
  // nazwą, żeby po przełączeniu wyboru dało się porównać zakresy; wybrany jest niżej.
  const mdcpOtherBuffers = useMemo(
    () => (mdcpData?.buffers ?? []).filter((b) => !b.is_active && b.geometry),
    [mdcpData],
  );
  const mdcpOrders = mdcpData?.orders ?? [];
  const [mdcpAddMode, setMdcpAddMode] = useState(false);
  const [mdcpBufferEdit, setMdcpBufferEdit] = useState(false);
  // Rysowanie NOWEGO obrysu pod kolejne zamówienie MDCP: poligon klikany Geomanem,
  // po zamknięciu DOKŁADA się do listy obrysów projektu i staje się wybrany
  // (dawniej zastępował jedyny bufor, przez co zakres starszego zamówienia znikał).
  const [mdcpBufferDraw, setMdcpBufferDraw] = useState(false);
  const [mdcpSelected, setMdcpSelected] = useState<string | null>(null);
  // Zamrożone obrysy zamówień MDCP — do podpisania na mapie, który obrys jest którego
  // zamówienia. Identyczne geometrie (typowo: bufor niezmieniony między zamówieniami)
  // składają się w jeden kształt z listą numerów — jak przy zamówieniach MZ niżej.
  const mdcpOrderShapes = useMemo(() => {
    const frozen = new Map<string, { geometry: GeoJsonGeometry; numbers: string[] }>();
    for (const o of mdcpOrders) {
      if (!o.buffer_geometry) continue;
      const key = JSON.stringify(o.buffer_geometry);
      const entry = frozen.get(key);
      if (entry) entry.numbers.push(o.order_number);
      else frozen.set(key, { geometry: o.buffer_geometry, numbers: [o.order_number] });
    }
    return frozen;
  }, [mdcpOrders]);
  // Geometrie żywych obrysów z listy — zamrożony obrys zamówienia identyczny z którymś
  // z nich rysuje warstwa obrysu (z nazwą), więc tu go pomijamy, żeby nie kreskować dwa razy.
  const mdcpLiveBufferKeys = useMemo(
    () => new Set(mdcpOtherBuffers.map((b) => JSON.stringify(b.geometry))),
    [mdcpOtherBuffers],
  );
  // The buffer overlay AND the photo points are only drawn while the MDCP panel is expanded
  // (reported by the card) — starts false so nothing flashes before the card reports.
  const [mdcpPanelExpanded, setMdcpPanelExpanded] = useState(false);

  // ── Mapa zasadnicza (MZ): zamawiana z tej zakładki, bo zakres wniosku = obrys MDCP
  // tego projektu. Lista modułowa (/power-projects/mz) zostaje, tu jest skrót „z projektu".
  // with_buffer=1 dokłada zamrożony obrys każdego zamówienia — rysujemy go na mapie.
  const { data: mzOrders } = useMzOrders({ connection: connection.id, with_buffer: "1" });
  const [mzModalOpen, setMzModalOpen] = useState(false);
  // Sekcja MZ zwija się jak panel MDCP, a mapa rysuje zakres tylko gdy jest rozwinięta.
  // Domyślnie rozwinięta dopóki nic nie zamówiono (wtedy warto zobaczyć, co pójdzie do
  // starostwa); po zamówieniu startuje zwinięta, żeby nie zaśmiecać widoku trasy.
  const [mzExpandedOverride, setMzExpandedOverride] = useState<boolean | null>(null);
  const mzExpanded = mzExpandedOverride ?? (mzOrders != null && mzOrders.length === 0);
  // Co rysujemy jako „bufor zamówienia MZ": obrysy zamrożone w aktywnych zamówieniach
  // (kolejne zamówienia mrożą zwykle ten sam obrys → deduplikacja po geometrii), a zanim
  // wniosek zostanie wygenerowany — bieżący obrys MDCP, czyli zakres, który się zamrozi.
  const mzScope = useMemo(() => {
    const active = (mzOrders ?? []).filter((o) => o.status !== "cancelled");
    const frozen = new Map<
      string,
      { geometry: GeoJsonGeometry; numbers: string[]; bufferName: string }
    >();
    for (const o of active) {
      if (!o.buffer_geometry) continue;
      const key = JSON.stringify(o.buffer_geometry);
      const entry = frozen.get(key);
      if (entry) entry.numbers.push(o.number);
      else frozen.set(key, {
        geometry: o.buffer_geometry, numbers: [o.number], bufferName: o.buffer_name || "",
      });
    }
    if (frozen.size) return { frozen: true, shapes: [...frozen.values()] };
    const geometry = mdcpBuffer?.geometry ?? null;
    if (!geometry) return { frozen: false, shapes: [] };
    return {
      frozen: false,
      shapes: [{
        geometry,
        numbers: active.map((o) => o.number),
        bufferName: mdcpBuffer?.display_name ?? "",
      }],
    };
  }, [mzOrders, mdcpBuffer]);

  // Extra hide of the MDCP photo points while the panel IS expanded (declutters e.g. reading
  // the point list over the route). Add-mode forces them back on — placing/dragging points
  // with the markers hidden makes no sense.
  // Warstwa powierzchni do wyłączenia — domyślnie wyłączona, bo dotyczy tylko projektów
  // idących na odrolnienie; włącznik obok pozostałych nakładek.
  const [showOdrolnienie, setShowOdrolnienie] = useState(false);
  const [odrolnienieGeo, setOdrolnienieGeo] = useState<GeoJSON.FeatureCollection | null>(null);
  useEffect(() => {
    if (!showOdrolnienie || odrolnienieGeo) return;
    getOdrolnienieFootprints(connection.id)
      .then(setOdrolnienieGeo)
      .catch(() => setOdrolnienieGeo({ type: "FeatureCollection", features: [] }));
  }, [showOdrolnienie, odrolnienieGeo, connection.id]);

  const [showMdcpPoints, setShowMdcpPoints] = usePersistentToggle(
    MDCP_POINTS_VISIBLE_STORAGE_KEY,
    true,
  );
  const toggleMdcpPoints = () => setShowMdcpPoints(!showMdcpPoints);

  // „Zdjęcia z terenu": geotagged result-file photos of this project's orders, with the
  // distance to the nearest ordered parcel/object. Fetched only while the layer is on.
  const [showFieldPhotos, setShowFieldPhotos] = usePersistentToggle(
    FIELD_PHOTOS_VISIBLE_STORAGE_KEY,
    false,
  );
  const { data: fieldPhotosData } = useQuery({
    queryKey: ["pp", "field-photos", connection.id],
    queryFn: () => getFieldPhotos(connection.id),
    enabled: showFieldPhotos,
    staleTime: 30_000,
  });
  const fieldPhotos = useMemo(() => fieldPhotosData?.photos ?? [], [fieldPhotosData]);

  // Druga warstwa tej samej opcji: punkty zdjęciowe MDCP, do których geodeta wgrał zdjęcia.
  // Ten materiał nie ma geotagu (wgrywka idzie przez klik w punkt, nie przez EXIF), więc bez
  // tego byłby na mapie nieobecny — a to on jest podstawą odbioru zlecenia geodezyjnego.
  // Dane są już w pamięci: panel MDCP pobiera punkty razem ze zdjęciami, więc warstwa nie
  // kosztuje dodatkowego żądania.
  const mdcpPhotoPoints = useMemo(() => mdcpPointsWithPhotos(mdcpPoints), [mdcpPoints]);
  // Płaska lista WSZYSTKIEGO, co warstwa pokazuje: najpierw materiał punktów MDCP (tak jak
  // w panelu po prawej — te same podpisy i nazwy plików), potem geotagowane pliki wynikowe.
  // Jedna lista, bo dla oglądającego to jeden zbiór zdjęć z terenu: strzałkami przewija się
  // cały materiał projektu, niezależnie od tego, którą miniaturę się kliknęło.
  const layerPhotos = useMemo(
    () => [...buildMdcpLightbox(mdcpPhotoPoints), ...buildFieldPhotoLightbox(fieldPhotos)],
    [mdcpPhotoPoints, fieldPhotos],
  );
  const layerPhotoIndexById = useMemo(
    () => new Map(layerPhotos.map((ph, i) => [ph.id, i] as const)),
    [layerPhotos],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // Stabilny mostek z miniatury w dymku (DOM Leafleta) do stanu Reacta — tak samo jak
  // przycisk rozmowy WPW niżej; handler popupopen rejestrujemy raz, przy tworzeniu mapy.
  const openPhotoRef = useRef<(photoId: string) => void>(() => {});
  openPhotoRef.current = (photoId: string) => {
    const idx = layerPhotoIndexById.get(photoId);
    if (idx !== undefined) setLightboxIndex(idx);
  };
  // Zgaszenie warstwy zamyka podgląd — inaczej zdjęcia zostałyby na ekranie bez znaczników,
  // z których wyszły.
  useEffect(() => {
    if (!showFieldPhotos) setLightboxIndex(null);
  }, [showFieldPhotos]);

  // Map click-tools (mutually exclusive with MDCP add-mode):
  //   "info"       = geoportal informator — click a point → the działka there, from ULDK
  //   "streetview" = two-phase click (point → direction) → open Google Street View
  //   "measure"    = distance measurement — clicks build a polyline, dblclick ends it
  const [mapTool, setMapTool] = useState<"none" | "info" | "streetview" | "measure">("none");
  // Running total (metres) of the measure tool's committed vertices — for the hint bar.
  const [measureTotal, setMeasureTotal] = useState<number | null>(null);
  // Arm a tool, always leaving MDCP add-mode and buffer-draw first (they own the map click).
  const armTool = (t: "info" | "streetview" | "measure") => {
    setMdcpAddMode(false);
    setMdcpBufferDraw(false);
    setMapTool((cur) => (cur === t ? "none" : t));
  };

  // Default the "Wgraj wersję" form to the project's current state — the variant and
  // preset of the currently viewed route version — so adding a new version doesn't
  // require re-picking what's already in use. Fills only an empty field (never clobbers
  // a manual choice) and waits until the relevant data has loaded.
  useEffect(() => {
    if (variantId) return;
    const def = selectedVersion?.variant || variants?.[0]?.id;
    if (def) setVariantId(def);
  }, [variantId, selectedVersion?.variant, variants]);

  useEffect(() => {
    if (layerPresetId || !presets) return;
    const cur = selectedVersion?.layer_preset;
    if (cur && presets.some((p) => p.id === cur && p.is_active))
      setLayerPresetId(cur);
  }, [layerPresetId, selectedVersion?.layer_preset, presets]);

  const dxfInvalid = !!dxfFile && !isDxf(dxfFile);
  // When presets exist the designer must pick the one their map follows.
  const uploadReady =
    !!variantId && !!dxfFile && !dxfInvalid && (!hasPresets || !!layerPresetId);

  const selectedVariant = (variants ?? []).find((v) => v.id === variantId) ?? null;

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);
  // MDCP buffer lives in its OWN feature group (not the redrawn overlay) so the Geoman edit
  // handles survive background refreshes of parcels/route.
  const bufferRef = useRef<L.FeatureGroup | null>(null);
  const mzBufferRef = useRef<L.FeatureGroup | null>(null);
  // Holds the raw-mode overlay — either the live WMS layer or a static XYZ tile layer.
  const wmsRef = useRef<L.TileLayer | null>(null);
  const osmRef = useRef<L.TileLayer | null>(null);
  const gugikParcelsRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikInfraRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikOrthoRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikMpzpRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGdosRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorEwidencjaRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGddkiaRef = useRef<GddkiaDataLayer | null>(null);
  const gugikClosedAreasRef = useRef<ClosedAreasLayer | null>(null);
  // Tracks the (version, mode) the view was last auto-fitted to, so the auto-fit runs
  // once per version/mode — never on background data refreshes or overlay toggles.
  const lastFitRef = useRef<string>("");
  // True when the map was initialised from a viewport remembered earlier this session, so
  // the mount-time auto-fit is skipped for the initial (version, mode) — the user is
  // returning to the tab and expects the same view. A later version/mode change re-fits.
  const restoredViewportRef = useRef(false);
  // Informator: temporary dashed outline of the last identified działka (cleared on the
  // next click or when the tool is turned off).
  const infoHighlightRef = useRef<L.Layer | null>(null);
  // Live set of TERYT-y already in the project — read from inside the (rarely re-armed)
  // informator popup handler to decide "Dodaj działkę" vs "już w systemie" without
  // re-binding the map-click effect on every parcels refresh.
  const parcelTerytSetRef = useRef<Set<string>>(new Set());
  // Street View tool: the placed anchor + its rubber-band direction line (phase 1 → 2).
  const svRef = useRef<{ anchor: L.LatLng; marker: L.CircleMarker; line: L.Polyline | null } | null>(null);

  // Depend on isLoading: on the first visit the loading early-return means the
  // container div is not yet in the DOM when this effect first runs, so it bails
  // out. Re-running once isLoading flips to false (container now mounted) creates
  // the map; the guard keeps it single-init. Without this the map only appears
  // after leaving and re-entering the tab (when data is already cached).
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    // Restore the viewport the user last looked at this session (see saveMapViewport):
    // switching tabs remounts the map, and without this it would jump back to the auto-fit.
    const saved = loadMapViewport(connection.id);
    restoredViewportRef.current = saved != null;
    const map = L.map(containerRef.current, {
      center: saved ? [saved.lat, saved.lng] : [52.0, 19.0],
      zoom: saved ? saved.zoom : 6,
      attributionControl: false,
    });
    osmRef.current = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 21,
        // Keep OSM as the BASE: an explicit low zIndex pins it under the raw-DXF WMS
        // (zIndex 1). Without it, re-adding OSM (its raw-mode toggle) re-inserts it last
        // and it covers the DXF.
        zIndex: 0,
      },
    ).addTo(map);
    overlayRef.current = L.layerGroup().addTo(map);
    bufferRef.current = L.featureGroup().addTo(map);
    mzBufferRef.current = L.featureGroup().addTo(map);
    // Polish tooltips for Geoman's vertex-edit handles (buffer editing).
    map.pm.setLang("pl");

    // GUGiK reference overlays in dedicated panes (above OSM + raw-DXF WMS, below the curated
    // vectors at overlayPane 400): one layer for działki (granice + numery), one for GESUT.
    const g = createGugikLayers(map);
    gugikParcelsRef.current = g.parcels;
    gugikInfraRef.current = g.infra;
    gugikOrthoRef.current = g.ortho;
    gugikMpzpRef.current = g.mpzp;
    gugikGdosRef.current = g.gdos;
    gugikKonserwatorRef.current = g.konserwator;
    gugikKonserwatorEwidencjaRef.current = g.konserwatorEwidencja;
    gugikGddkiaRef.current = g.gddkia;
    gugikClosedAreasRef.current = g.closedAreas;
    map.on("zoomend", () => setZoom(map.getZoom()));
    // Remember the view across tab switches — moveend fires after both pan and zoom.
    map.on("moveend", () => saveMapViewport(connection.id, map));
    // Wire the "Pokaż rozmowę WPW" button inside any opened popup to the React modal.
    map.on("popupopen", (e: L.PopupEvent) => {
      const btn = e.popup
        .getElement()
        ?.querySelector<HTMLElement>("[data-wpw-parcel]");
      if (!btn) return;
      btn.addEventListener("click", () =>
        openWpwRef.current(
          btn.dataset.wpwParcel ?? "",
          btn.dataset.wpwLabel ?? "WPW",
        ),
      );
    });
    // Każda miniatura w dymku (kadr punktu MDCP albo geotagowany plik wynikowy) otwiera się
    // na pełnym ekranie — jednym mechanizmem dla obu źródeł warstwy „Zdjęcia z terenu",
    // żeby na mapie nie było zdjęć, które zachowują się inaczej niż sąsiednie.
    map.on("popupopen", (e: L.PopupEvent) => {
      const root = e.popup.getElement();
      root?.querySelectorAll<HTMLElement>("[data-photo-id]").forEach((thumb) => {
        // Leaflet potrafi pokazać ten sam element dymka ponownie — bez znacznika każde
        // kolejne otwarcie dokładałoby drugi nasłuch do tej samej miniatury.
        if (thumb.dataset.photoWired) return;
        thumb.dataset.photoWired = "1";
        thumb.addEventListener("click", (ev) => {
          ev.preventDefault();
          openPhotoRef.current(thumb.dataset.photoId ?? "");
        });
      });
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [isLoading]);

  useEffect(() => {
    const map = mapRef.current;
    const group = overlayRef.current;
    if (!map || !group) return;
    group.clearLayers();

    // While MDCP add-mode OR a map click-tool (informator / street view) is armed, the
    // vector overlays must NOT capture the click — it has to fall through to the map so the
    // tool can act on the raw point (drop a photo point / identify the działka under it).
    const suppressOverlayClicks = mdcpAddMode || mdcpBufferEdit || mdcpBufferDraw || mapTool !== "none";

    // Collected while drawing: every on-parcel number label (to nudge off overlapping objects)
    // and every point-like object/photo-point that could cover one (obstacles, in latlng).
    const labelEntries: ParcelLabelEntry[] = [];
    const obstaclePts: L.LatLng[] = [];
    // Map projection for the label pole-of-inaccessibility maths (defaults to Web Mercator).
    const crs = map.options.crs ?? L.CRS.EPSG3857;

    // Colored parcels by status — shared by "Uznane" and the raw "Działki" toggle.
    // Notification parcels (demontaż — tylko zawiadomienie) are marked light green regardless
    // of their resolution status; spare parcels (na zapas — no route / object on them) get
    // their own sand fill + dashed outline so they never read as "uzgodniona" (see
    // parcelFillColor).
    const drawParcels = () => {
      for (const p of parcels ?? []) {
        if (!p.geometry || !isProjectParcel(p)) continue;
        const fillColor = parcelFillColor(p);
        const layer = L.geoJSON(p.geometry as never, {
          style: p.is_used
            ? { color: "#555", weight: 1, fillColor, fillOpacity: 0.4 }
            : { color: "#78716c", weight: 1, dashArray: "4 3", fillColor, fillOpacity: 0.45 },
          // A click-tool is armed (MDCP add / informator / street view): parcels must NOT
          // capture the click (no popup) — the click falls through to the map's tool handler.
          interactive: !suppressOverlayClicks,
        });
        if (!suppressOverlayClicks) {
          layer.bindPopup(parcelPopupHtml(p, wpwByOwner, proceedingsByParcel.get(p.id) ?? [], connection.project), {
            maxWidth: 320,
            minWidth: 200,
          });
          // Drive the right-hand history panel off the popup lifecycle. The functional
          // guard on close handles the A→B switch (A's popupclose may race B's popupopen)
          // and the overlay redraw (clearLayers closes the popup → panel restores).
          layer.on("popupopen", () => setHistoryParcelId(p.id));
          layer.on("popupclose", () =>
            setHistoryParcelId((prev) => (prev === p.id ? null : prev)),
          );
        }
        // Permanent, non-interactive number label with the ownership-group, so the grupa
        // własności reads at a glance without opening the popup. Placed at the parcel's pole
        // of inaccessibility (labelCandidates[0]) — always inside the outline, even on a long
        // or narrow działka where the bbox centre would sit outside. Added unconditionally: it
        // is display-only and never captures clicks, so it is safe while a click-tool is armed.
        // Collected into labelEntries with its fallback anchors so positionLabels() can shift
        // it off any object that would cover it. (The private-land hatch draws over the fill.)
        const groupLabel = parcelGroupLabel(p.ownership_code);
        const rings = groupLabel ? projectedParcelRings(p.geometry, crs) : null;
        if (rings) {
          const candidates = labelCandidates(rings).map(([x, y]) =>
            crs.unproject(L.point(x, y)),
          );
          const marker = L.marker(candidates[0], {
            icon: parcelGroupIcon(esc(groupLabel)),
            interactive: false,
            keyboard: false,
            // Above object markers (600) so the number stays readable where they meet.
            zIndexOffset: 700,
          });
          marker.addTo(group);
          const { hw, hh } = labelHalfExtent(groupLabel);
          labelEntries.push({ marker, candidates, hw, hh });
        }
        layer.addTo(group);

        // Prywatne działki: kolor liczony jak dotąd, ale nakładamy na wierzch
        // czarny wzorek (diagonalna kreska), żeby od razu było widać, że to
        // grunt prywatny (isPrivateParcel — ta sama reguła zasila legendę).
        // Warstwa nakładki jest nieinteraktywna — klik i popup obsługuje
        // kolorowa działka pod spodem.
        const isPrivate = isPrivateParcel(p);
        // Działka podmiotu przyłączanego (flaga ustawiana przez koordynatora / dyrektora
        // na zakładce Działki) dostaje własny wzór — kreska przerywana w przeciwnym
        // kierunku, więc na prywatnej działce przyłączanego widać oba wzory naraz.
        const overlays: HatchSpec[] = [];
        if (isPrivate) overlays.push(PRIVATE_HATCH);
        if (p.is_connected_entity) overlays.push(CONNECTED_HATCH);
        for (const spec of overlays) {
          const hatch = L.geoJSON(p.geometry as never, {
            style: { stroke: false, fillOpacity: 1 },
            interactive: false,
          });
          hatch.addTo(group);
          ensureHatchPattern(map, spec);
          hatch.eachLayer((l) => {
            const path = (l as unknown as { _path?: SVGPathElement })._path;
            path?.setAttribute("fill", `url(#${spec.id})`);
          });
        }
      }
    };
    // Powierzchnie do wyłączenia z produkcji rolniczej — prostokąty ze stawek, obrócone
    // jak urządzenie. Nieinteraktywne: klik ma trafić w działkę pod spodem.
    const drawOdrolnienie = () => {
      if (!showOdrolnienie || !odrolnienieGeo?.features?.length) return;
      const layer = L.geoJSON(odrolnienieGeo as never, {
        style: { color: "#c31f1f", weight: 1, fillOpacity: 1 },
        interactive: false,
      });
      layer.addTo(group);
      ensureHatchPattern(map, ODROLNIENIE_HATCH);
      layer.eachLayer((l) => {
        const path = (l as unknown as { _path?: SVGPathElement })._path;
        path?.setAttribute("fill", `url(#${ODROLNIENIE_HATCH.id})`);
      });
    };
    drawOdrolnienie();

    // Recognized route: per-type overlay (typed_lines) styled exactly like the tender
    // drawing (kolor/kreska z ZEElementType); types with no ZEElementType row get a
    // slug-derived fallback (voltage hue, pale demolition, overhead dash). The flat red
    // route + demolition geometries are drawn only for old versions without typed_lines.
    // In MDCP add-mode all route overlays are non-interactive too, so a click on a line
    // still drops a point rather than being swallowed.
    const routeInteractive = !suppressOverlayClicks;
    const demolitionStyle = { color: "#2563eb", weight: 3, dashArray: "6 4" };
    const drawRoute = () => {
      const typedFeatures = selectedVersion?.typed_lines?.features ?? [];
      // Red base route + blue demolition overlay: only as a fallback for old
      // versions parsed before per-type classification. typed_lines is a complete
      // cover of `geometry` (demolition segments included), so with typed features
      // present we must NOT lay solid lines underneath the typed overlay — they
      // bleed through the gaps of dashed styles (e.g. "napowietrzna"), making the
      // map disagree with the legend swatch.
      if (typedFeatures.length === 0) {
        if (selectedVersion?.geometry) {
          L.geoJSON(selectedVersion.geometry as never, {
            style: { color: "#dc2626", weight: 3 },
            interactive: routeInteractive,
          }).addTo(group);
        }
        if (selectedVersion?.demolition_geometry) {
          L.geoJSON(selectedVersion.demolition_geometry as never, {
            style: demolitionStyle,
            interactive: routeInteractive,
          }).addTo(group);
        }
      }
      // Types without a matching ZEElementType row: style derived from the slug
      // itself (voltage hue, pale demolition, overhead dash), so sn/nn and
      // demontaż never share a colour even when the dictionary doesn't know the type.
      for (const f of typedFeatures) {
        if (etByCode.has(f.properties.semantic_type)) continue;
        const layer = L.geoJSON(f as never, {
          style: fallbackLineStyle(f.properties.semantic_type),
          interactive: routeInteractive,
        });
        if (routeInteractive) {
          layer.bindTooltip(f.properties.semantic_type.replace(/_/g, " ") || "trasa");
        }
        layer.addTo(group);
      }
      for (const f of typedFeatures) {
        const et = etByCode.get(f.properties.semantic_type);
        if (!et) continue;
        const layer = L.geoJSON(f as never, {
          style: lineStyleFor(et),
          interactive: routeInteractive,
        });
        if (routeInteractive) layer.bindTooltip(et.label);
        layer.addTo(group);
      }
    };
    // Parsed map objects — points/blocks the system recognized from the DXF. When the
    // semantic_type matches a ZEElementType code, use the tender symbol (shape/fill/colour);
    // otherwise fall back to the hash-derived style.
    const drawMapObjects = () => {
      for (const o of selectedVersion?.objects ?? []) {
        if (!o.geometry) continue;
        const et = etByCode.get(o.semantic_type);
        const icon = et
          ? L.divIcon({
              html: objectSvg(et, 16),
              className: "pp-object-icon",
              iconSize: [16, 16],
              iconAnchor: [8, 8],
            })
          : objectIcon(o.semantic_type);
        const layer = L.geoJSON(o.geometry as never, {
          pointToLayer: (_f, ll) => L.marker(ll, { icon, interactive: !suppressOverlayClicks }),
          style: { color: objectColor(o.semantic_type), weight: 2 },
          // Same as parcels — while a click-tool is armed, object markers/lines let the
          // click through so the tool acts on the point instead of showing a tooltip.
          interactive: !suppressOverlayClicks,
        });
        if (!suppressOverlayClicks) {
          layer.bindTooltip(et ? et.label : o.semantic_type || "obiekt");
        }
        // A point object sits on top of a parcel — record it so a number it covers can be
        // nudged aside (lines/blocks are elongated and rarely hide the central number).
        if (o.geometry.type === "Point") {
          const [lng, lat] = o.geometry.coordinates as [number, number];
          obstaclePts.push(L.latLng(lat, lng));
        }
        layer.addTo(group);
      }
    };
    // MDCP photo points (numbered circle + azimuth arrow); draggable while in add-mode
    // and not yet handed to the surveyor.
    const drawMdcpPoints = () => {
      for (const p of mdcpPoints) {
        if (!p.geometry) continue;
        const [lng, lat] = p.geometry.coordinates as [number, number];
        obstaclePts.push(L.latLng(lat, lng));
        const m = L.marker([lat, lng], {
          icon: mdcpIcon(p, p.id === mdcpSelected),
          draggable: mdcpAddMode && !p.order_link,
          zIndexOffset: 1000,
        });
        // Click = "direction gesture": nudge the azimuth 45° clockwise (0°→45°→…→315°→0°)
        // and persist, so aiming the photo direction is a tap on the map, not a keyboard
        // entry in the panel. Only while editing (add-mode) and before the point is frozen
        // at the surveyor; otherwise a click just selects it (syncs the panel highlight).
        m.on("click", () => {
          setMdcpSelected(p.id);
          if (!mdcpAddMode || p.order_link) return;
          const next = (p.azimuth_deg + 45) % 360;
          updateMdcpPoint(p.id, { azimuth_deg: next }).finally(() =>
            qc.invalidateQueries({ queryKey: mdcpQueryKey(connection.id) }),
          );
        });
        m.on("dragend", async () => {
          const ll = m.getLatLng();
          try {
            await updateMdcpPoint(p.id, { lat: ll.lat, lng: ll.lng });
          } finally {
            qc.invalidateQueries({ queryKey: mdcpQueryKey(connection.id) });
          }
        });
        m.bindTooltip(
          `Punkt foto #${p.number} · ${p.azimuth_deg}°${p.note ? ` · ${p.note}` : ""}${p.is_done ? " · wykonane" : ""}`,
        );
        m.addTo(group);
      }
    };

    // „Zdjęcia z terenu": one camera pin per geotagged result photo; the popup carries the
    // thumbnail, capture time and the distance to the nearest ordered parcel/object so the
    // reviewer checks "was it taken on the right działka" at a glance.
    const drawFieldPhotos = () => {
      for (const p of fieldPhotos) {
        const [lng, lat] = p.geometry.coordinates;
        const m = L.marker([lat, lng], {
          icon: fieldPhotoIcon(p),
          zIndexOffset: 900,
          interactive: !suppressOverlayClicks,
        });
        m.bindTooltip(
          `${p.original_name}${p.nearest ? ` · ${Math.round(p.nearest.distance_m)} m od ${p.nearest.label}` : ""}`,
        );
        m.bindPopup(fieldPhotoPopupHtml(p), { maxWidth: 260 });
        m.addTo(group);
      }
    };

    // Ta sama warstwa, drugie źródło: punkt zdjęciowy MDCP z materiałem od geodety. Znacznik
    // stoi z boku punktu (przy rozwiniętym panelu MDCP w samym punkcie jest już jego pinezka).
    // Klik otwiera dymek z LISTĄ miniatur, bo na punkt bywa kilka zdjęć — dopiero klik
    // w konkretną miniaturę idzie na pełny ekran.
    const drawMdcpPhotoBadges = () => {
      for (const p of mdcpPhotoPoints) {
        if (!p.geometry) continue;
        const [lng, lat] = p.geometry.coordinates as [number, number];
        const m = L.marker([lat, lng], {
          icon: mdcpPhotoBadgeIcon(p),
          // Nad pinezką punktu (1000) i nad geotagami (900) — to znacznik, w który się klika.
          zIndexOffset: 1100,
          interactive: !suppressOverlayClicks,
        });
        m.bindTooltip(mdcpPhotoBadgeTooltip(p));
        m.bindPopup(mdcpPointPhotosPopupHtml(p), { maxWidth: 280 });
        m.addTo(group);
      }
    };

    // Photo points are OFF unless the MDCP panel on the right is expanded (they clutter the
    // route view), and they also respect the toolbar toggle. Add-mode always shows them —
    // the user is actively placing/dragging them, so hiding would be self-defeating.
    const mdcpVisible = (mdcpPanelExpanded && showMdcpPoints) || mdcpAddMode;
    if (mode === "curated") {
      drawParcels();
      drawRoute();
      drawMapObjects();
      if (mdcpVisible) drawMdcpPoints();
    } else {
      // Raw: the DXF comes from the WMS; overlays are opt-in via the checkboxes.
      if (rawShowParcels) drawParcels();
      if (rawShowObjects) {
        drawRoute();
        drawMapObjects();
      }
      if (mdcpVisible) drawMdcpPoints();
    }
    if (showFieldPhotos) {
      drawFieldPhotos();
      drawMdcpPhotoBadges();
    }

    // Shift any parcel number a map object covers to its first fallback anchor that is clear,
    // keeping it on the parcel — so the grupa własności stays readable. Overlap is a pixel
    // test, so it depends on zoom (panning preserves the label↔object pixel vector, zooming
    // does not) → rerun on zoomend. Each label re-evaluates from its pole every pass, so it
    // snaps back once nothing overlaps. When there are no objects (the common case) every
    // label just sits at its pole.
    const OBSTACLE_RADIUS = 12; // px half-size of an object marker/icon
    const positionLabels = () => {
      if (!labelEntries.length) return;
      const obstaclePx = obstaclePts.map((ll) => map.latLngToLayerPoint(ll));
      const covers = (px: L.Point, hw: number, hh: number) =>
        obstaclePx.some(
          (o) =>
            Math.abs(o.x - px.x) < hw + OBSTACLE_RADIUS &&
            Math.abs(o.y - px.y) < hh + OBSTACLE_RADIUS,
        );
      for (const e of labelEntries) {
        // Pole first, then interior fallbacks: take the first anchor no object covers. If all
        // are covered, stay at the pole (on the parcel, just overlapped — better than off it).
        let chosen = e.candidates[0];
        if (obstaclePx.length) {
          for (const cand of e.candidates) {
            if (!covers(map.latLngToLayerPoint(cand), e.hw, e.hh)) {
              chosen = cand;
              break;
            }
          }
        }
        e.marker.setLatLng(chosen);
      }
    };
    positionLabels();
    map.on("zoomend", positionLabels);

    // The view is fitted by the auto-fit effect below (and the "Przybliż do projektu"
    // button) — not here — so background data refreshes never yank the viewport.
    return () => {
      map.off("zoomend", positionLabels);
    };
  }, [
    parcels,
    selectedVersion,
    mode,
    rawShowParcels,
    rawShowObjects,
    wpwByOwner,
    proceedingsByParcel,
    etByCode,
    mdcpPoints,
    mdcpAddMode,
    mdcpBufferEdit,
    mdcpBufferDraw,
    mdcpSelected,
    showMdcpPoints,
    mdcpPanelExpanded,
    mapTool,
    // Zdjęcia z terenu: bez tych dwóch włączenie warstwy nie przerysowałoby nakładek.
    fieldPhotos,
    showFieldPhotos,
    // …a bez tego nowe zdjęcie punktu MDCP nie zmieniłoby jego znacznika ani listy miniatur.
    mdcpPhotoPoints,
    // Bez tych dwóch klik w przycisk odrolnienia tylko podświetlał przycisk: stan się zmieniał,
    // ale ten efekt (jedyny, który rysuje nakładki) nie przebiegał, więc prostokąty pojawiały
    // się dopiero przy przypadkowym przerysowaniu z innego powodu.
    showOdrolnienie,
    odrolnienieGeo,
    connection.id,
    connection.project,
    qc,
  ]);

  // MDCP buffer (obrys zamówienia): półprzezroczysty poligon w dedykowanej warstwie. W trybie
  // edycji włączamy geoman na wierzchołkach i zapisujemy obrys po każdej zmianie. Warstwa jest
  // osobno od `overlayRef`, więc odświeżenia działek/trasy nie gubią uchwytów edycji.
  useEffect(() => {
    const map = mapRef.current;
    const group = bufferRef.current;
    if (!map || !group) return;
    group.clearLayers();
    // Show the buffer (obrys dla geodety) only when the MDCP panel is expanded.
    if (!mdcpPanelExpanded) return;
    const geom = mdcpBuffer?.geometry ?? null;
    const liveKey = geom ? JSON.stringify(geom) : null;

    // Zamrożone obrysy zamówień MDCP: kreskowane, tylko-do-odczytu, podpisane numerem
    // zamówienia. Kształt identyczny z bieżącym buforem pomijamy — narysuje go warstwa
    // „żywa" niżej z numerami w podpisie (podwójna kreska w tym samym miejscu brudzi mapę).
    // Pozostałe obrysy z listy projektu: szara kreska + nazwa (i numery zamówień, które
    // na nich stoją) — dawniej po narysowaniu nowego obrysu stary w ogóle nie istniał.
    for (const other of mdcpOtherBuffers) {
      const otherLayer = L.geoJSON(other.geometry as never, {
        style: { color: "#78716c", weight: 1.5, dashArray: "3 5", fillColor: "#a8a29e", fillOpacity: 0.04 },
        pmIgnore: true,
        interactive: false,
      });
      otherLayer.bindTooltip(
        other.order_numbers.length
          ? `${other.display_name} — zlecone: ${other.order_numbers.join(", ")}`
          : `${other.display_name} (niewybrany)`,
        { permanent: true, direction: "center", opacity: 0.7, className: "text-[10px]" },
      );
      otherLayer.addTo(group);
    }

    for (const [key, shape] of mdcpOrderShapes) {
      if (key === liveKey || mdcpLiveBufferKeys.has(key)) continue;
      const frozenLayer = L.geoJSON(shape.geometry as never, {
        style: {
          color: "#ea580c",
          weight: 2,
          dashArray: "6 4",
          fillColor: "#f97316",
          fillOpacity: 0.05,
        },
        // Nieklikalny jak obrysy MZ niżej — nie może zabierać kliknięć działkom pod spodem.
        pmIgnore: true,
        interactive: false,
      });
      frozenLayer.bindTooltip(`MDCP ${shape.numbers.join(", ")} — zamrożony obrys zamówienia`, {
        permanent: true,
        direction: "center",
        opacity: 0.85,
      });
      frozenLayer.addTo(group);
    }

    if (!geom) return;
    const layer = L.geoJSON(geom as never, {
      style: {
        color: "#ea580c",
        weight: 2,
        fillColor: "#f97316",
        fillOpacity: mdcpBufferEdit ? 0.06 : 0.12,
      },
      pmIgnore: !mdcpBufferEdit,
      interactive: mdcpBufferEdit,
    });
    layer.addTo(group);
    if (!mdcpBufferEdit) {
      // Podpis bieżącego buforu: numery zamówień, których zakres to dokładnie ten obrys;
      // po narysowaniu nowego obrysu pod kolejne zamówienie — wyraźne „jeszcze niezlecony".
      const name = mdcpBuffer?.display_name ?? "Bufor MDCP";
      const ordered = mdcpBuffer?.order_numbers ?? [];
      if (ordered.length) {
        layer.bindTooltip(`${name} — wybrany; zlecone: ${ordered.join(", ")}`, {
          permanent: true,
          direction: "center",
          opacity: 0.85,
        });
      } else if (mdcpOrderShapes.size > 0 || mdcpOtherBuffers.length > 0) {
        layer.bindTooltip(`${name} — wybrany obrys (jeszcze niezlecony)`, {
          permanent: true,
          direction: "center",
          opacity: 0.85,
        });
      } else {
        layer.bindTooltip('Bufor MDCP — włącz „Edytuj bufor", by przesuwać wierzchołki', { sticky: true });
      }
      return;
    }
    // Edit mode: enable per-vertex editing and persist the reshaped outline on every change.
    let saving = false;
    const persist = async () => {
      if (saving) return;
      const fc = layer.toGeoJSON() as GeoJSON.FeatureCollection;
      const g = fc.features[0]?.geometry;
      if (!g) return;
      // Removing the outline's vertices one by one empties the polygon instead of deleting
      // it (Geoman detaches it from the map but not from this group), and it would persist
      // as coordinates: []. That is not an edit the user asked to save — see
      // components/map/geometry.ts for the full mechanism.
      if (isEmptyGeometry(g)) return;
      if (!mdcpBuffer?.id) return;
      saving = true;
      try {
        await saveMdcpBuffer(connection.id, mdcpBuffer.id, g as never);
      } finally {
        saving = false;
        qc.invalidateQueries({ queryKey: mdcpQueryKey(connection.id) });
      }
    };
    layer.eachLayer((l) => {
      const path = l as L.Layer & { pm?: { enable: (o: unknown) => void; disable: () => void } };
      path.pm?.enable({ allowSelfIntersection: false });
      l.on("pm:edit", persist);
      l.on("pm:markerdragend", persist);
    });
    return () => {
      layer.eachLayer((l) => {
        const path = l as L.Layer & { pm?: { disable: () => void } };
        path.pm?.disable();
      });
    };
  }, [mdcpBuffer, mdcpBufferEdit, mdcpPanelExpanded, mdcpOrderShapes, mdcpOtherBuffers,
      mdcpLiveBufferKeys, connection.id, qc]);

  // Rysowanie nowego obrysu (zakres KOLEJNEGO zamówienia MDCP): Geomanowy poligon; po
  // zamknięciu kształt DOKŁADA się do listy obrysów projektu jako nowa pozycja i staje się
  // wybrany. Poprzednie obrysy zostają — razem z zamrożonymi zakresami swoich zamówień.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mdcpBufferDraw) return;
    map.pm.enableDraw("Polygon", { allowSelfIntersection: false });
    const onCreate = async (e: { layer: L.Layer }) => {
      const feature = (e.layer as L.Polygon).toGeoJSON();
      // Geoman leaves the drawn layer on the map — the saved buffer re-renders through
      // the effect above, so the temporary layer would only double the outline.
      map.removeLayer(e.layer);
      setMdcpBufferDraw(false);
      const g = feature.geometry;
      if (!g || isEmptyGeometry(g)) return;
      try {
        await createMdcpBuffer(connection.id, { geometry: g as never });
      } finally {
        qc.invalidateQueries({ queryKey: mdcpQueryKey(connection.id) });
      }
    };
    map.on("pm:create", onCreate);
    return () => {
      map.off("pm:create", onCreate);
      map.pm.disableDraw();
    };
  }, [mdcpBufferDraw, connection.id, qc]);

  // Zakres zamówienia MZ — jak bufor MDCP, tylko tylko do odczytu (obrys jest zamrożony w
  // zamówieniu, poprawia się go przez MDCP i ponowną generację) i w innym kolorze, żeby dało
  // się je odróżnić, gdy oba panele są rozwinięte. Rysowany wyłącznie przy rozwiniętej sekcji MZ.
  useEffect(() => {
    const map = mapRef.current;
    const group = mzBufferRef.current;
    if (!map || !group) return;
    group.clearLayers();
    if (!mzExpanded) return;
    for (const shape of mzScope.shapes) {
      const layer = L.geoJSON(shape.geometry as never, {
        style: {
          color: "#7c3aed",
          weight: 2,
          dashArray: "6 4",
          fillColor: "#8b5cf6",
          fillOpacity: 0.08,
        },
        // Nieklikalny: obrys przykrywa cały korytarz trasy, więc łapanie kliknięć
        // zabrałoby dostęp do działek pod spodem (opis obrysu jest w sekcji MZ obok).
        pmIgnore: true,
        interactive: false,
      });
      // Podpis na mapie: który obrys jest którego zamówienia (permanent działa też na
      // warstwie nieinteraktywnej — sticky by nie zadziałał bez zdarzeń myszy).
      const fromBuffer = shape.bufferName ? ` z obrysu „${shape.bufferName}"` : "";
      layer.bindTooltip(
        mzScope.frozen && shape.numbers.length
          ? `MZ ${shape.numbers.join(", ")} — zakres zamówienia${fromBuffer}`
          : `MZ — planowany zakres wniosku${fromBuffer || " (wybrany obrys MDCP)"}`,
        { permanent: true, direction: "center", opacity: 0.85 },
      );
      layer.addTo(group);
    }
  }, [mzScope, mzExpanded]);

  // MDCP add-mode: a map click drops a new photo point (auto-numbered, azimuth 0 — set
  // in the side panel). Crosshair cursor signals the mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mdcpAddMode) return;
    const el = map.getContainer();
    el.style.cursor = "crosshair";
    const onClick = async (e: L.LeafletMouseEvent) => {
      try {
        await createMdcpPoint({
          connection: connection.id,
          lat: e.latlng.lat,
          lng: e.latlng.lng,
          azimuth_deg: 0,
        });
      } finally {
        qc.invalidateQueries({ queryKey: mdcpQueryKey(connection.id) });
      }
    };
    map.on("click", onClick);
    return () => {
      map.off("click", onClick);
      el.style.cursor = "";
    };
  }, [mdcpAddMode, connection.id, qc]);

  // Keep the informator's "already in system" lookup fresh without re-arming the map click.
  useEffect(() => {
    parcelTerytSetRef.current = new Set(
      (parcels ?? []).map((p) => p.teryt).filter((t): t is string => !!t),
    );
  }, [parcels]);

  // ── Geoportal informator: click a point → identify the działka there via ULDK ──────
  // Works ANYWHERE, independently of our own działki (backend queries GetParcelByXY at
  // the clicked lat/lng). Opens a Leaflet popup at the point and outlines the parcel.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mapTool !== "info") return;
    const el = map.getContainer();
    el.style.cursor = "crosshair";

    const clearHighlight = () => {
      if (infoHighlightRef.current) {
        try { map.removeLayer(infoHighlightRef.current); } catch { /* detached */ }
        infoHighlightRef.current = null;
      }
    };

    // Monotonic id so a slow ULDK response from an earlier click can't overwrite a newer one;
    // `cancelled` drops any response that lands after the tool is switched off.
    let reqId = 0;
    let cancelled = false;
    let openPopup: L.Popup | null = null;
    const onClick = async (e: L.LeafletMouseEvent) => {
      clearHighlight();
      const myReq = ++reqId;
      const popup = L.popup({ maxWidth: 340, minWidth: 210 })
        .setLatLng(e.latlng)
        .setContent(
          `<div style="font-family:ui-monospace,monospace;font-size:12px;color:#555;padding:2px 0">Pobieranie z geoportalu…</div>`,
        )
        .openOn(map);
      openPopup = popup;
      let info: UldkPointInfo | null = null;
      try {
        info = await getUldkAtPoint(e.latlng.lat, e.latlng.lng);
      } catch {
        info = null;
      }
      if (cancelled || myReq !== reqId || !mapRef.current) return; // stale / tool off / unmounted
      if (!info || !info.teryt) {
        popup.setContent(
          `<div style="font-family:ui-monospace,monospace;font-size:12px;color:#6b7280;padding:2px 0">Brak działki w tym miejscu (geoportal).</div>`,
        );
        return;
      }
      const teryt = info.teryt;
      const alreadyInSystem = !!teryt && parcelTerytSetRef.current.has(teryt);
      popup.setContent(uldkPopupHtml(info, e.latlng, alreadyInSystem));
      if (info.geometry) {
        const hl = L.geoJSON(info.geometry as never, {
          interactive: false,
          style: { color: "#7c3aed", weight: 2.5, opacity: 0.9, fillColor: "#7c3aed", fillOpacity: 0.1, dashArray: "6 4" },
        });
        hl.addTo(map);
        infoHighlightRef.current = hl;
      }
      // Wire "Dodaj działkę do systemu" (the popup is plain HTML, outside React). Reuses the
      // manual-add endpoint (create/revive-if-exists) then best-effort attaches ULDK geometry
      // so the działka lands on the map as a real parcel colored by status.
      if (teryt && !alreadyInSystem) {
        const btn = popup
          .getElement()
          ?.querySelector<HTMLButtonElement>(`#${ADD_PARCEL_BTN_ID}`);
        btn?.addEventListener("click", async () => {
          btn.disabled = true;
          btn.style.opacity = "0.7";
          btn.textContent = "Dodawanie…";
          try {
            const created = await createParcel({
              connection: connection.id,
              teryt,
              short_number: info.parcel || "",
              obreb: info.region || "",
              voivodeship: info.voivodeship || "",
              county: info.county || "",
              commune: info.commune || "",
            });
            // Geometry/area/ownership are a separate best-effort GUGiK call — a flaky
            // service must not undo the add; the user can re-fetch it later per parcel.
            try {
              await fetchParcelUldk(created.id);
            } catch {
              /* geometry can be fetched later from the parcel row */
            }
            parcelTerytSetRef.current.add(teryt);
            qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
            btn.textContent = "✓ Dodano do systemu";
            btn.style.background = "#059669";
            btn.style.borderColor = "#059669";
            btn.style.opacity = "1";
          } catch {
            btn.disabled = false;
            btn.style.opacity = "1";
            btn.textContent = "Błąd — spróbuj ponownie";
          }
        });
      }
    };

    map.on("click", onClick);
    return () => {
      cancelled = true;
      map.off("click", onClick);
      clearHighlight();
      if (openPopup) { try { map.closePopup(openPopup); } catch { /* detached */ } }
      el.style.cursor = "";
    };
  }, [mapTool]);

  // ── Street View tool: two-phase click (position → direction → open panorama) ───────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mapTool !== "streetview") return;
    map.getContainer().style.cursor = "crosshair";

    const clearSv = () => {
      if (svRef.current) {
        map.removeLayer(svRef.current.marker);
        if (svRef.current.line) map.removeLayer(svRef.current.line);
        svRef.current = null;
      }
    };

    const onClick = (e: L.LeafletMouseEvent) => {
      if (!svRef.current) {
        const marker = L.circleMarker(e.latlng, {
          radius: 8, color: "#fff", weight: 2, fillColor: "#ef4444", fillOpacity: 1,
        }).addTo(map);
        svRef.current = { anchor: e.latlng, marker, line: null };
      } else {
        const { anchor } = svRef.current;
        const heading = Math.round(headingBetween(anchor, e.latlng));
        window.open(
          `https://maps.google.com/maps?layer=c&cbll=${anchor.lat},${anchor.lng}&cbp=11,${heading},0,0,0`,
          "_blank",
          "noopener,noreferrer",
        );
        clearSv();
        setMapTool("none");
      }
    };
    const onMove = (e: L.LeafletMouseEvent) => {
      if (!svRef.current) return;
      if (svRef.current.line) {
        svRef.current.line.setLatLngs([svRef.current.anchor, e.latlng]);
      } else {
        svRef.current.line = L.polyline([svRef.current.anchor, e.latlng], {
          color: "#3b82f6", weight: 2, dashArray: "6,4",
        }).addTo(map);
      }
    };

    map.on("click", onClick);
    map.on("mousemove", onMove);
    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMove);
      clearSv();
      map.getContainer().style.cursor = "";
    };
  }, [mapTool]);

  // ── Measure tool: clicks build a polyline; every vertex carries the running total and a
  // dashed rubber-band segment previews the distance to the cursor. Dblclick ends the
  // measurement (the line stays for reading); the next click starts a fresh one. Turning
  // the tool off clears everything. Distances are geodesic (map.distance = haversine).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mapTool !== "measure") return;
    const el = map.getContainer();
    el.style.cursor = "crosshair";
    // Dblclick finishes a measurement — it must not zoom while the tool is armed.
    const hadDblClickZoom = map.doubleClickZoom.enabled();
    map.doubleClickZoom.disable();

    const layers = L.layerGroup().addTo(map);
    let pts: L.LatLng[] = [];
    let markers: L.CircleMarker[] = [];
    let line: L.Polyline | null = null;
    let rubber: L.Polyline | null = null;
    let finished = false;

    const total = (arr: L.LatLng[]) => {
      let s = 0;
      for (let i = 1; i < arr.length; i++) s += map.distance(arr[i - 1], arr[i]);
      return s;
    };
    const clearRubber = () => {
      if (rubber) {
        layers.removeLayer(rubber);
        rubber = null;
      }
    };

    const onClick = (e: L.LeafletMouseEvent) => {
      if (finished) {
        // A click after dblclick starts a new measurement from scratch.
        layers.clearLayers();
        pts = [];
        markers = [];
        line = null;
        rubber = null;
        finished = false;
        setMeasureTotal(null);
      }
      pts.push(e.latlng);
      const mk = L.circleMarker(e.latlng, {
        radius: 4,
        color: "#fff",
        weight: 1.5,
        fillColor: "#0ea5e9",
        fillOpacity: 1,
      }).addTo(layers);
      markers.push(mk);
      if (!line) {
        line = L.polyline(pts, { color: "#0ea5e9", weight: 3 }).addTo(layers);
      } else {
        line.setLatLngs(pts);
      }
      const t = total(pts);
      if (pts.length > 1) {
        mk.bindTooltip(fmtDistance(t), {
          permanent: true,
          direction: "top",
          offset: L.point(0, -6),
          className: "pp-measure-label",
        }).openTooltip();
      }
      setMeasureTotal(t);
    };
    const onMove = (e: L.LeafletMouseEvent) => {
      if (finished || pts.length === 0) return;
      const last = pts[pts.length - 1];
      if (!rubber) {
        rubber = L.polyline([last, e.latlng], {
          color: "#0ea5e9",
          weight: 2,
          dashArray: "5 5",
          interactive: false,
        }).addTo(layers);
        rubber.bindTooltip("", {
          permanent: true,
          direction: "top",
          offset: L.point(0, -8),
          className: "pp-measure-label",
        });
      } else {
        rubber.setLatLngs([last, e.latlng]);
      }
      rubber.setTooltipContent(fmtDistance(total(pts) + map.distance(last, e.latlng)));
      rubber.getTooltip()?.setLatLng(e.latlng);
    };
    const onDblClick = () => {
      // A dblclick also fired two click events, so the last vertex is duplicated — drop it.
      if (pts.length >= 2 && pts[pts.length - 1].equals(pts[pts.length - 2])) {
        pts.pop();
        const dup = markers.pop();
        if (dup) layers.removeLayer(dup);
        line?.setLatLngs(pts);
      }
      clearRubber();
      finished = true;
      setMeasureTotal(total(pts));
    };

    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("dblclick", onDblClick);
    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMove);
      map.off("dblclick", onDblClick);
      map.removeLayer(layers);
      if (hadDblClickZoom) map.doubleClickZoom.enable();
      el.style.cursor = "";
      setMeasureTotal(null);
    };
  }, [mapTool]);

  // Open Geoportal on the CURRENT map view. The new viewer takes a centre + scale; the scale
  // is derived server-side from the extent's metric size (no proj4 in the SPA) and THIS map's
  // pixel size, so the whole view fits — same as the ZE map.
  const openGeoportal = async () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    const size = map.getSize();
    try {
      const { url } = await getGeoportalUrl({
        sw_lng: sw.lng, sw_lat: sw.lat, ne_lng: ne.lng, ne_lat: ne.lat,
        width_px: Math.round(size.x), height_px: Math.round(size.y),
      });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      window.alert("Nie udało się otworzyć Geoportalu dla tego widoku.");
    }
  };

  // OSM base toggle — controllable in raw mode, always on in "Uznane".
  useEffect(() => {
    const map = mapRef.current;
    const osm = osmRef.current;
    if (!map || !osm) return;
    const show = mode !== "raw" || rawShowOsm;
    if (show) {
      if (!map.hasLayer(osm)) osm.addTo(map);
    } else if (map.hasLayer(osm)) {
      map.removeLayer(osm);
    }
  }, [mode, rawShowOsm]);

  // GUGiK reference overlays — available in both modes, driven by the panel checkboxes.
  useGugikToggle(mapRef, gugikParcelsRef, gugik.parcels);
  useGugikToggle(mapRef, gugikInfraRef, gugik.infra);
  useGugikToggle(mapRef, gugikOrthoRef, gugik.ortho);
  useGugikToggle(mapRef, gugikMpzpRef, gugik.mpzp);
  useGugikToggle(mapRef, gugikGdosRef, gugik.gdos);
  useGugikToggle(mapRef, gugikKonserwatorRef, !!gugik.konserwator);
  useGugikToggle(mapRef, gugikKonserwatorEwidencjaRef, !!gugik.konserwatorEwidencja);
  useGugikToggle(mapRef, gugikGddkiaRef, !!gugik.gddkia);
  useGugikToggle(mapRef, gugikClosedAreasRef, !!gugik.closedAreas);
  const gugikLoading = useGugikLoading(
    gugikParcelsRef,
    gugikInfraRef,
    gugikOrthoRef,
    gugik,
    gugikMpzpRef,
    gugikGdosRef,
    gugikKonserwatorRef,
    gugikKonserwatorEwidencjaRef,
    gugikGddkiaRef,
    gugikClosedAreasRef,
  );

  // Raw mode: overlay the full DXF (+ mapa zasadnicza) as a WMS from the PyQGIS-built
  // QGZ. The curated `parcels` layer is excluded; mapa zasadnicza is drawn UNDER the
  // DXF (WMS renders LAYERS left→right as bottom→top).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (wmsRef.current) {
      map.removeLayer(wmsRef.current);
      wmsRef.current = null;
    }
    setWmsLoading(false);
    if (mode !== "raw") return;

    // Guard against a superseded run: if the deps change (version/mode) while the
    // async layer resolution below is in flight, `cancelled` stops it from attaching
    // an orphaned overlay to the map.
    let cancelled = false;
    const attach = (layer: L.TileLayer) => {
      if (cancelled) return;
      layer.on("loading", () => setWmsLoading(true));
      layer.on("load", () => setWmsLoading(false));
      layer.addTo(map);
      setWmsLoading(true);
      wmsRef.current = layer;
    };

    // Fast path: static XYZ tiles (gdal2tiles → S3) — plain L.tileLayer straight from the
    // CDN, no QGIS Server. Only heavy drawings have a tiled COG; the rest fall through to
    // the live WMS below. (Beyond maxNativeZoom Leaflet upscales — blur — instead of 404.)
    if (wms?.raster_tiles_url) {
      attach(L.tileLayer(`${wms.raster_tiles_url}/{z}/{x}/{y}.png`, {
        maxNativeZoom: wms.raster_tiles_max_zoom ?? undefined,
        maxZoom: 21,
        updateWhenIdle: true,
        updateWhenZooming: false,
        zIndex: 1,
      }));
      return () => { cancelled = true; };
    }
    const gpId = selectedVersion?.gis_project_id;
    if (!gpId || !wms?.wms_url) return;
    apiClient
      .get<{ data: { layers?: { name: string }[] } }>(`/gis/projects/${gpId}/`)
      .then((r) => {
        if (cancelled) return;
        const ord = (n: string) => (n === "basemap" ? 0 : n === "dxf" ? 2 : 1);
        const names = (r.data.data.layers || [])
          .map((l) => l.name)
          .filter((n) => n !== "parcels")
          .sort((a, b) => ord(a) - ord(b))
          .join(",");
        if (!names) return;
        attach(L.tileLayer.wms(wms.wms_url, {
          layers: names,
          format: "image/png",
          transparent: true,
          version: "1.3.0",
          tileSize: 512,
          maxZoom: 21,
          zIndex: 1,
        }));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [wms, selectedVersion, mode]);

  const startUpload = () => {
    if (!uploadReady || !dxfFile) return;
    upload.mutate(
      {
        variant: variantId,
        dxf: dxfFile,
        layerPreset: layerPresetId || null,
        comment: comment.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          setDxfFile(null);
          setComment("");
          // Surface the background pipeline (parse → preview → parcels) so the whole
          // wait is visible as a live server log. The new version is auto-selected when
          // the task completes (see the task-completion effect).
          setTaskId(res.task_id);
        },
      },
    );
  };

  const doUpload = async () => {
    if (!uploadReady || !dxfFile) return;
    if (!askBeforeUpload) {
      startUpload();
      return;
    }
    // Monit: dry-run the layer check first; the actual upload waits for AKCEPTUJ.
    setValidating(true);
    setValidationError(null);
    try {
      setValidation(await validateDxf(dxfFile, layerPresetId || null));
    } catch (e) {
      setValidationError(apiErr(e));
    } finally {
      setValidating(false);
    }
  };

  // Zakładka mapy przelicza z kontrolą nałożeń (check_overlaps): próbkuje całą trasę,
  // także pod istniejącymi działkami, i oddaje zmiany w ewidencji do decyzji w oknie.
  const doRecompute = async () => {
    if (!selectedVersion) return;
    const r = await recomputeVersion(selectedVersion.id, { checkOverlaps: true });
    setTaskId(r.task_id);
  };

  // Retry the sidecar render from the stored DXF (no re-upload) — surfaces the same
  // live task log as an upload; the completion effect refreshes gis_project_id.
  const doRebuildPreview = async () => {
    if (!selectedVersion) return;
    const r = await rebuildVersionPreview(selectedVersion.id);
    setTaskId(r.task_id);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else wrapperRef.current?.requestFullscreen?.();
  };

  // Fit the view to the project's footprint — route + parcels (in route / manual) +
  // map objects — works in both modes and from any zoom (e.g. after fullscreen).
  // Returns true only when a valid footprint existed and the view was moved, so the
  // auto-fit effect can tell a real fit apart from "no data yet" and retry.
  const fitToProject = (): boolean => {
    const map = mapRef.current;
    if (!map) return false;
    const parts: L.LatLngBounds[] = [];
    const add = (g: unknown) => {
      if (!g) return;
      const b = L.geoJSON(g as never).getBounds();
      if (b.isValid()) parts.push(b);
    };
    for (const p of parcels ?? []) {
      if (p.geometry && isProjectParcel(p)) add(p.geometry);
    }
    add(selectedVersion?.geometry);
    for (const o of selectedVersion?.objects ?? []) add(o.geometry);
    if (parts.length === 0 && mode === "raw" && wms?.extent_3857) {
      // No vector geometry (e.g. the DXF layers didn't map to the preset) —
      // fall back to the raw-preview extent reported by the builder, otherwise
      // the button has nothing to fit to and the world view stays.
      const [minx, miny, maxx, maxy] = wms.extent_3857;
      parts.push(
        L.latLngBounds(
          L.CRS.EPSG3857.unproject(L.point(minx, miny)),
          L.CRS.EPSG3857.unproject(L.point(maxx, maxy)),
        ),
      );
    }
    if (parts.length === 0) return false;
    const bounds = parts.slice(1).reduce((acc, b) => acc.extend(b), parts[0]);
    map.fitBounds(bounds, { padding: [30, 30] });
    return true;
  };
  // Keep the latest fitToProject reachable from the auto-fit effect without making it
  // a dependency (its identity changes every render).
  const fitToProjectRef = useRef(fitToProject);
  fitToProjectRef.current = fitToProject;

  // Entering the "Mapa / Trasa" tab remounts this component, so on every visit (and on
  // a version/mode change) snap the view to the project footprint — exactly what the
  // "Przybliż do projektu" button does. Deferred one frame and preceded by
  // invalidateSize so the freshly-mounted container has its real dimensions before we
  // fit; otherwise the fit is computed against a stale/zero size and the map stays at
  // the default Poland-wide view. Fits once per (version, mode): lastFitRef is set only
  // on a successful fit, so an attempt made before the data has loaded is retried when
  // parcels/version arrive, while later background refreshes never re-fit.
  useEffect(() => {
    if (isLoading || !mapRef.current) return;
    const fitKey = `${selectedVersion?.id ?? ""}|${mode}`;
    if (lastFitRef.current === fitKey) return;
    const raf = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;
      map.invalidateSize();
      // A viewport restored this session wins over the mount-time auto-fit: keep the
      // remembered view (only the container size is fixed above) and mark this key handled
      // so background refreshes don't snap away. A later version/mode switch re-fits.
      if (restoredViewportRef.current) {
        restoredViewportRef.current = false;
        lastFitRef.current = fitKey;
        return;
      }
      if (fitToProjectRef.current()) lastFitRef.current = fitKey;
    });
    return () => cancelAnimationFrame(raf);
    // wms w zależnościach: extent podglądu dociera asynchronicznie — ponów fit,
    // gdy przyjdzie (wersje bez geometrii wektorowej fitują po extencie rastra).
  }, [isLoading, parcels, selectedVersion, mode, wms]);

  // Track native fullscreen state.
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // When fullscreen flips, wait for the browser to apply the new layout, then tell
  // Leaflet to recompute its size so tiles fill the whole container (no white gaps).
  // invalidateSize keeps the current centre, so the location does not move.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => map.invalidateSize()),
    );
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [isFullscreen]);

  if (isLoading)
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );

  // Sekcje MDCP (punkty zdjęciowe, obrysy, zlecenie geodety) i mapy zasadniczej — w zwykłym
  // projekcie w prawej kolumnie, w trybie prowadzenia przez firmę POD mapą (cała szerokość).
  const mdcpAndMzCards = (
    <>
        <MdcpSectionCard
          connectionId={connection.id}
          addMode={mdcpAddMode}
          onToggleAddMode={(v) => {
            if (v) { setMapTool("none"); setMdcpBufferEdit(false); setMdcpBufferDraw(false); } // share the map click
            setMdcpAddMode(v);
          }}
          bufferEdit={mdcpBufferEdit}
          onToggleBufferEdit={(v) => {
            if (v) { setMapTool("none"); setMdcpAddMode(false); setMdcpBufferDraw(false); } // buffer edit owns the map too
            setMdcpBufferEdit(v);
          }}
          bufferDraw={mdcpBufferDraw}
          onToggleBufferDraw={(v) => {
            if (v) { setMapTool("none"); setMdcpAddMode(false); setMdcpBufferEdit(false); } // drawing owns the map too
            setMdcpBufferDraw(v);
          }}
          selectedId={mdcpSelected}
          onSelect={setMdcpSelected}
          onExpandedChange={setMdcpPanelExpanded}
        />
        <SectionCard
          title="Mapa zasadnicza (MZ)"
          action={
            <button
              type="button"
              className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
              title={mzExpanded ? "Zwiń sekcję (zdejmuje zakres z mapy)" : "Rozwiń sekcję (pokazuje zakres na mapie)"}
              onClick={() => setMzExpandedOverride(!mzExpanded)}
            >
              {mzExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          }
          bodyClassName={mzExpanded ? undefined : "hidden"}
        >
          {mzScope.shapes.length > 0 && (
            <p className="mb-2 flex items-start gap-1.5 text-[11px] leading-snug text-[var(--color-text-muted)]">
              <span
                className="mt-1 inline-block h-2.5 w-2.5 shrink-0 border border-dashed"
                style={{ borderColor: "#7c3aed", backgroundColor: "rgba(139,92,246,0.15)" }}
              />
              {mzScope.frozen
                ? `Fioletowy obrys na mapie = zakres zamówienia${
                    mzScope.shapes.length === 1 && mzScope.shapes[0].numbers.length
                      ? ` (${mzScope.shapes[0].numbers.join(", ")})`
                      : ""
                  }, zamrożony przy generacji wniosku.`
                : `Fioletowy obrys na mapie = wybrany obrys MDCP${
                    mdcpBuffer?.display_name ? ` („${mdcpBuffer.display_name}")` : ""
                  } — taki zakres pójdzie do starostwa; zamrozi się przy generacji wniosku.`}
            </p>
          )}
          {(mzOrders ?? []).length > 0 ? (
            <ul className="space-y-1.5 mb-2">
              {(mzOrders ?? []).map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-2 text-xs">
                  <Link
                    to={`/power-projects/mz/${o.id}`}
                    className="font-mono font-bold hover:underline truncate"
                  >
                    {o.number}
                  </Link>
                  {/* Z którego obrysu wniosek bierze zakres — projekt ma ich wiele,
                      a po fakcie nie da się tego wyczytać z samego numeru. */}
                  {o.buffer_name && (
                    <span
                      className="truncate text-[var(--color-text-muted)]"
                      title={`Zakres wniosku z obrysu „${o.buffer_name}"`}
                    >
                      {o.buffer_name}
                    </span>
                  )}
                  <MzStatusBadge status={o.status} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] mb-2">
              Brak zamówień mapy zasadniczej dla tego projektu. Zakresem wniosku jest obrys
              MDCP, dołączany do wniosku jako DXF.
            </p>
          )}
          <Button className="w-full" variant="ghost" onClick={() => setMzModalOpen(true)}>
            <MapPinned size={13} className="mr-1.5" />
            Zamów mapę zasadniczą
          </Button>
        </SectionCard>
    </>
  );

  return (
    <div className="space-y-5">
    <style>{`
      /* On-parcel ownership-group number. The wrapper is a zero-size divIcon anchored at the
         parcel's pole; the badge is translated to centre on that point (so a setLatLng nudge
         keeps it centred). Reset the default .leaflet-div-icon white box/border. */
      .leaflet-div-icon.pp-parcel-group-label-wrap {
        background: transparent;
        border: none;
        width: 0;
        height: 0;
        overflow: visible;
      }
      .pp-parcel-group-badge {
        position: absolute;
        left: 0;
        top: 0;
        transform: translate(-50%, -50%);
        background: rgba(255,255,255,0.9);
        border: 1px solid rgba(0,0,0,0.15);
        border-radius: 3px;
        color: #111827;
        font-family: ui-monospace, monospace;
        font-size: 11px;
        font-weight: 700;
        padding: 0 4px;
        line-height: 1.4;
        pointer-events: none;
        white-space: nowrap;
        box-shadow: 0 1px 2px rgba(0,0,0,0.12);
      }
      /* Measure-tool distance labels (per-vertex running total + rubber-band preview). */
      .leaflet-tooltip.pp-measure-label {
        background: rgba(255,255,255,0.92);
        border: 1px solid rgba(0,0,0,0.2);
        border-radius: 3px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.15);
        font-family: ui-monospace, monospace;
        font-size: 11px;
        font-weight: 700;
        color: #0c4a6e;
        padding: 0 4px;
      }
      .leaflet-tooltip.pp-measure-label::before { display: none; }
    `}</style>
    <div className={led && !historyParcel ? "grid gap-5 items-start" : "grid lg:grid-cols-[1fr_320px] gap-5 items-start"}>
      <DeleteRouteVersionModal
        connection={connection.id}
        version={versionToDelete}
        successor={versionToDeleteSuccessor}
        singleVariant={!multiVariant}
        onClose={() => setVersionToDelete(null)}
        onDeleted={(v, variantDeleted) => {
          // The preview falls back to the current version; a variant that went with its
          // last version must leave the upload form's selector too.
          if (selectedVersionId === v.id) setSelectedVersionId(null);
          if (variantDeleted && variantId === v.variant) setVariantId("");
          setVersionToDelete(null);
        }}
      />
      {editVersionOpen && selectedVersion && (
        <EditRouteVersionModal
          version={selectedVersion}
          connectionId={connection.id}
          projectSrid={connection.srid}
          onClose={() => setEditVersionOpen(false)}
          onTask={setTaskId}
        />
      )}
      {wpwModal && (
        <WpwConversationModal
          title={wpwModal.label}
          parcelId={wpwModal.parcelId}
          onClose={() => setWpwModal(null)}
        />
      )}
      {mzModalOpen && (
        <CreateMzOrderModal
          connectionId={connection.id}
          onClose={() => setMzModalOpen(false)}
        />
      )}
      <div className="space-y-4">
        {/* Projekt prowadzony przez firmę: trasę wgrywa się tak jak u firmy — jeden
            wariant, bez listy wersji; po wgraniu odświeżamy zapytania warsztatu. */}
        {led && (
          <RouteUploadPanel
            leadershipId={led.leadershipId}
            tutorialHref={led.tutorialHref}
            hasRoute={led.hasRoute}
            dxf={led.dxf}
            onUploaded={() => qc.invalidateQueries({ queryKey: ["pp"] })}
          />
        )}
        {/* `isolate` contains the Leaflet/GUGiK z-indexes (panes + our z-[1000]
            controls) inside the map box, so body-level overlays (Modal z-50,
            notifications dropdown) paint above the map — ZEDrawingMap precedent. */}
        <div
          ref={wrapperRef}
          className={`relative isolate z-0 ${isFullscreen ? "bg-white" : ""}`}
        >
          <div
            ref={containerRef}
            className="w-full border border-[var(--color-border)]"
            style={{
              background: "#fff",
              height: isFullscreen ? "100vh" : "560px",
            }}
          />
          {mode === "raw" && wmsLoading && (
            <div className="absolute bottom-11 left-2 z-[1000] pointer-events-none flex items-center gap-1.5 bg-white border border-[var(--color-border)] shadow-sm px-2 py-1 font-mono text-[11px] text-[var(--color-text-secondary)]">
              <Spinner />
              DXF…
            </div>
          )}
          {/* Display-mode toggle: curated (recognized, styled) vs raw full DXF */}
          <div className="absolute top-2 right-2 z-[1000] flex border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-sm font-mono text-[11px] uppercase tracking-wider">
            {(["curated", "raw"] as const).map((mkey) => (
              <button
                key={mkey}
                type="button"
                onClick={() => setMode(mkey)}
                className={
                  mode === mkey
                    ? "px-2.5 py-1 bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                    : "px-2.5 py-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                }
              >
                {mkey === "curated" ? "Uznane" : "Surowy DXF"}
              </button>
            ))}
          </div>
          {/* GUGiK reference overlays — działki (granice + numery) + GESUT, one toggle each.
              Always available (both modes); offset below the Leaflet zoom control (top-left). */}
          <GugikLayerPanel
            gugik={gugik}
            onChange={setGugik}
            zoom={zoom}
            loading={gugikLoading}
            className="absolute top-[78px] left-2"
          />
          {/* Fullscreen toggle */}
          <button
            type="button"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Zamknij pełny ekran" : "Pełny ekran"}
            aria-label={isFullscreen ? "Zamknij pełny ekran" : "Pełny ekran"}
            className="absolute bottom-2 right-2 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm p-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors"
          >
            {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          {/* Map toolbar: zoom-to-project + geoportal click-tools (informator / street view /
              open Geoportal). The click-tools work independently of our own działki. */}
          <div className="absolute bottom-2 left-2 z-[1000] flex items-stretch card bg-[var(--color-bg-surface)] shadow-sm overflow-hidden divide-x divide-[var(--color-border)]">
            <button
              type="button"
              onClick={fitToProject}
              title="Przybliż do projektu"
              className="px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors inline-flex items-center gap-1.5"
            >
              <Crosshair size={13} /> <span className="hidden sm:inline">Przybliż</span>
            </button>
            <button
              type="button"
              onClick={() => armTool("info")}
              title="Informator geoportalu — kliknij dowolne miejsce, aby zobaczyć działkę z ULDK (niezależnie od działek projektu)"
              className={`px-2 py-1 transition-colors inline-flex items-center ${
                mapTool === "info"
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              }`}
            >
              <Info size={14} />
            </button>
            <button
              type="button"
              onClick={() => armTool("streetview")}
              title="Street View — kliknij punkt, potem kierunek"
              className={`px-2 py-1 transition-colors inline-flex items-center ${
                mapTool === "streetview"
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              }`}
            >
              <PersonStanding size={14} />
            </button>
            <button
              type="button"
              onClick={() => armTool("measure")}
              title="Pomiar odległości — klikaj punkty, dwuklik kończy"
              className={`px-2 py-1 transition-colors inline-flex items-center ${
                mapTool === "measure"
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              }`}
            >
              <Ruler size={14} />
            </button>
            <button
              type="button"
              onClick={() => setShowOdrolnienie(!showOdrolnienie)}
              title={
                showOdrolnienie
                  ? "Ukryj powierzchnie do wyłączenia z produkcji rolniczej"
                  : "Pokaż powierzchnie do wyłączenia z produkcji rolniczej (te same prostokąty, "
                    + "które rysuje skrypt CAD i z których liczy się wniosek)"
              }
              className={`px-2 py-1 transition-colors inline-flex items-center ${
                showOdrolnienie
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              }`}
            >
              <Sprout size={14} />
            </button>
            <button
              type="button"
              onClick={toggleMdcpPoints}
              title={
                !mdcpPanelExpanded
                  ? "Punkty zdjęciowe (MDCP) — widoczne tylko przy rozwiniętym panelu MDCP po prawej"
                  : showMdcpPoints
                    ? "Ukryj punkty zdjęciowe (MDCP)"
                    : "Pokaż punkty zdjęciowe (MDCP)"
              }
              className={`px-2 py-1 transition-colors inline-flex items-center ${
                !showMdcpPoints
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              }`}
            >
              <MapPinOff size={14} />
            </button>
            <button
              type="button"
              onClick={() => setShowFieldPhotos(!showFieldPhotos)}
              title={
                showFieldPhotos
                  ? "Ukryj zdjęcia z terenu " +
                    `(geotagowane pliki wynikowe${fieldPhotosData ? `: ${fieldPhotos.length}` : ""}` +
                    ` · punkty MDCP ze zdjęciami: ${mdcpPhotoPoints.length})`
                  : "Pokaż zdjęcia z terenu — geotagowane pliki wynikowe zamówień z odległością od" +
                    " zleconych działek oraz punkty zdjęciowe MDCP (klik w punkt = podgląd jego zdjęć)"
              }
              className={`px-2 py-1 transition-colors inline-flex items-center ${
                showFieldPhotos
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              }`}
            >
              <Camera size={14} />
            </button>
            <button
              type="button"
              onClick={openGeoportal}
              title="Otwórz Geoportal dla bieżącego widoku"
              className="px-2 py-1 text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors inline-flex items-center"
            >
              <ExternalLink size={14} />
            </button>
          </div>
          {/* Active-tool hint (top-center; away from the mode toggle + GUGiK panel) */}
          {mapTool !== "none" && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm px-2.5 py-1 font-mono text-[11px] text-[var(--color-text-secondary)] whitespace-nowrap">
              {mapTool === "info"
                ? "Kliknij działkę — informator geoportalu"
                : mapTool === "streetview"
                  ? "Street View — kliknij punkt, potem kierunek"
                  : `Pomiar — klikaj punkty, dwuklik kończy${
                      measureTotal != null ? ` · razem: ${fmtDistance(measureTotal)}` : ""
                    }`}
            </div>
          )}
          {mode === "raw" && (
            <div className="absolute top-12 right-2 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm px-2.5 py-2 space-y-1.5 font-mono text-[11px] text-[var(--color-text-secondary)]">
              {[
                {
                  k: "parcels",
                  label: "Działki (kolory)",
                  val: rawShowParcels,
                  set: setRawShowParcels,
                },
                {
                  k: "objects",
                  label: "Odczytane obiekty",
                  val: rawShowObjects,
                  set: setRawShowObjects,
                },
                {
                  k: "osm",
                  label: "Podkład OSM",
                  val: rawShowOsm,
                  set: setRawShowOsm,
                },
              ].map((c) => (
                <label
                  key={c.k}
                  className="flex items-center gap-1.5 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    className="accent-[var(--color-primary)]"
                    checked={c.val}
                    onChange={(e) => c.set(e.target.checked)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          )}
          {mode === "raw" && !selectedVersion?.gis_project_id && (
            <div className="absolute bottom-11 left-2 z-[1000] card px-2 py-1 text-[11px] font-mono text-amber-600 flex items-center gap-2">
              {selectedVersion?.dxf_file ? (
                <>
                  Brak podglądu DXF dla tej wersji.
                  <button
                    type="button"
                    className="underline text-[var(--color-primary)] hover:opacity-80 disabled:opacity-50"
                    disabled={!!taskId && ["pending", "running"].includes(task.data?.status ?? "")}
                    onClick={doRebuildPreview}
                  >
                    Przebuduj podgląd
                  </button>
                </>
              ) : (
                <>Brak podglądu DXF dla tej wersji — wgraj DXF.</>
              )}
            </div>
          )}
          {/* Pusta mapa + przetarg z koncepcją → wyraźne CTA importu (DXF + typy + działki) */}
          {allVersions.length === 0 && (tenderVariants ?? []).length > 0 && (
            <div className="absolute inset-0 z-[900] flex items-center justify-center pointer-events-none">
              <div className="pointer-events-auto card bg-[var(--color-bg-surface)] shadow-lg border border-[var(--color-border)] p-4 max-w-sm space-y-2 text-center">
                <p className="text-sm font-semibold">Ten projekt nie ma jeszcze mapy</p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Zaimportuj koncepcję z przetargu, z którego pochodzi przedsięwzięcie —
                  trasa, obiekty (typy jak w przetargu), plik DXF i działki jednym ruchem.
                </p>
                <select
                  className="input-base w-full text-xs"
                  value={importZeVariantId}
                  onChange={(e) => setImportZeVariantId(e.target.value)}
                >
                  <option value="">— wybierz wariant przetargu —</option>
                  {(tenderVariants ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      Wariant {v.label} · {v.lines_count} linii, {v.objects_count} obiektów
                    </option>
                  ))}
                </select>
                {hasPresets && (
                  <select
                    className="input-base w-full text-xs"
                    value={importPresetId}
                    onChange={(e) => setImportPresetId(e.target.value)}
                    title="Warstwy pierwszej wersji mapy (dopasowanie po typie obiektu)"
                  >
                    <option value="">Warstwy przetargu (bez zmian)</option>
                    {activePresets.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
                {importError && (
                  <p className="text-[11px] font-mono text-[var(--color-primary)]">{importError}</p>
                )}
                <Button
                  className="w-full"
                  disabled={!importZeVariantId || taskBusy}
                  loading={importPending || taskBusy}
                  onClick={doImportFromTender}
                >
                  Zaimportuj koncepcję z przetargu
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* Legenda pod mapą — zwijana, domyślnie schowana. Tłumaczy WSZYSTKO, co mapa
            faktycznie rysuje: symbole linii i obiektów (język przetargu, też typy bez wpisu
            w słowniku) oraz kolory wypełnienia i kreskowanie działek. Działka „na zapas"
            i kreska podmiotu przyłączanego miały wcześniej własne paski zawsze widoczne pod
            mapą — wjechały tutaj, żeby cały klucz do mapy był w jednym miejscu. */}
        {(detectedLegend.length > 0 ||
          parcelLegend.fills.length > 0 ||
          parcelLegend.hatches.length > 0) && (
          <div className="text-[11px] text-[var(--color-text-secondary)]">
            <button
              type="button"
              onClick={() => setLegendOpen((o) => !o)}
              aria-expanded={legendOpen}
              className="inline-flex items-center gap-1 font-mono uppercase tracking-wide text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
            >
              {legendOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              Legenda (
              {detectedLegend.length + parcelLegend.fills.length + parcelLegend.hatches.length})
            </button>
            {legendOpen && (
              <div className="mt-1 space-y-1.5">
                {detectedLegend.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
                    <span className="font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
                      Linie i obiekty:
                    </span>
                    {detectedLegend.map((e) => (
                      <span key={e.key} className="inline-flex items-center gap-1.5">
                        <span dangerouslySetInnerHTML={{ __html: e.swatch }} />
                        {e.label}
                        {e.n > 0 && <span className="text-[var(--color-text-muted)]">×{e.n}</span>}
                      </span>
                    ))}
                  </div>
                )}
                {parcelLegend.fills.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
                    <span className="font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
                      Kolory działek:
                    </span>
                    {parcelLegend.fills.map((e) => (
                      <span
                        key={e.key}
                        className={`inline-flex items-center gap-1.5${e.title ? " cursor-help" : ""}`}
                        title={e.title}
                      >
                        <ParcelFillSwatch fill={e.fill} dashed={e.dashed} />
                        {e.label}
                        <span className="text-[var(--color-text-muted)]">×{e.n}</span>
                      </span>
                    ))}
                  </div>
                )}
                {parcelLegend.hatches.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
                    <span className="font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
                      Kreskowanie działek:
                    </span>
                    {parcelLegend.hatches.map((e) => (
                      <span key={e.key} className="inline-flex items-center gap-1.5 cursor-help" title={e.title}>
                        <HatchSwatch spec={e.spec} />
                        {e.label}
                        <span className="text-[var(--color-text-muted)]">×{e.n}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!led && (
          <RouteTimeline
            versions={allVersions}
            onDelete={setVersionToDelete}
            selectedId={selectedVersion?.id ?? null}
            onSelect={setSelectedVersionId}
            bindingAware={multiVariant}
          />
        )}
        {selectedVersion && (selectedVersion.dxf_layers?.length ?? 0) > 0 && (
          <DxfLayerInspector version={selectedVersion} />
        )}
        {led && mdcpAndMzCards}
      </div>

      <div className="space-y-4">
        {historyParcel && (
          <>
            <ParcelCasesCard
              parcel={historyParcel}
              proceedings={proceedingsByParcel.get(historyParcel.id) ?? []}
              wpwByOwner={wpwByOwner}
            />
            <ParcelHistoryCard
              connectionId={connection.id}
              parcel={historyParcel}
              onClose={() => {
                // Closing the popup fires popupclose → clears the state; the direct
                // reset covers the case where the popup is already gone.
                mapRef.current?.closePopup();
                setHistoryParcelId(null);
              }}
            />
          </>
        )}
        {/* Hidden, NOT unmounted, while the history shows — the cards keep their local
            state (MDCP panel, DXF upload form, task log) for when the parcel is deselected. */}
        <div className={historyParcel ? "hidden" : "space-y-4"}>
        {!led && mdcpAndMzCards}
        {!led && (
          <>
        <SectionCard title="Wersja trasy">
          <select
            className="input-base"
            value={selectedVersion?.id || ""}
            onChange={(e) => setSelectedVersionId(e.target.value)}
          >
            {allVersions.length === 0 && (
              <option value="">— brak wersji —</option>
            )}
            {allVersions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}{" "}
                {v.is_current
                  ? (!multiVariant || v.variantBinding ? "(aktualna)" : "(bieżąca w wariancie)")
                  : ""}{" "}
                {v.length_m ? `· ${Math.round(v.length_m)} m` : ""}
              </option>
            ))}
          </select>
          {selectedVersion && (
            <>
              <Button
                variant="secondary"
                className="w-full mt-3"
                disabled={!!taskId && ["pending", "running"].includes(task.data?.status ?? "")}
                onClick={() => setEditVersionOpen(true)}
                title="Edytuj preset, plik DXF, mapę zasadniczą i komentarz tej wersji"
              >
                <Pencil size={14} /> Edytuj wersję {selectedVersion.label}
              </Button>
              <Button
                className="w-full mt-2"
                onClick={doRecompute}
                title="Sprawdza wszystkie działki pod trasą w ULDK — także już obecne. Działka podzielona lub scalona w ewidencji trafia do okna decyzji, a nie na listę obok starej."
              >
                Przelicz działki (ULDK)
              </Button>
              <WpwStaleAfterRouteModal
                open={wpwStaleOpen}
                items={wpwStale}
                lettersHeld={wpwStaleHeld}
                onConfirm={() => setWpwStaleOpen(false)}
              />
              {!conflictsOpen && conflictGroups.length > 0 && conflictTaskId === taskId && (
                <button
                  type="button"
                  className="mt-1.5 w-full text-left text-[11px] text-amber-600 hover:underline leading-snug"
                  onClick={() => setConflictsOpen(true)}
                >
                  <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                  {conflictGroups.length === 1
                    ? "1 nałożenie działek czeka na decyzję — otwórz okno"
                    : `${conflictGroups.length} nałożeń działek czeka na decyzję — otwórz okno`}
                </button>
              )}
              {selectedTimelineVersion?.variantCanDelete && (
                <Button
                  variant="ghost"
                  className="w-full mt-2 text-[var(--color-danger)]"
                  disabled={taskBusy}
                  onClick={() => setVersionToDelete(selectedTimelineVersion)}
                  title={`Usuń wersję ${selectedTimelineVersion.label} — ostatnia wersja usuwa cały wariant`}
                >
                  <Trash2 size={14} /> Usuń wersję {selectedTimelineVersion.label}
                </Button>
              )}
            </>
          )}
          {selectedVersion?.dxf_file && (
            <div className="mt-2">
              <Button
                variant="secondary"
                className="w-full"
                disabled={!!taskId && ["pending", "running"].includes(task.data?.status ?? "")}
                onClick={doRebuildPreview}
                title="Buduje od nowa surowy podgląd DXF (QGIS) z zapisanego pliku — bez re-uploadu"
              >
                ⟲ Przebuduj podgląd (QGIS)
              </Button>
              <p className="text-[11px] text-[var(--color-text-muted)] mt-1 leading-snug">
                Odświeża surowy podgląd tej wersji — m.in. nakłada aktualne kolory
                wykrytych typów obiektów i typowane linie.
              </p>
            </div>
          )}
          {offshoreCount > 0 && (
            <div className="mt-2">
              <Button
                variant="secondary"
                className="w-full"
                loading={refetchingOcean}
                onClick={doRefetchOcean}
              >
                <AlertTriangle size={14} /> Napraw działki poza mapą ({offshoreCount})
              </Button>
              <p className="text-[11px] text-amber-600 mt-1 leading-snug">
                {offshoreCount === 1 ? "1 działka ma" : `${offshoreCount} działek ma`} geometrię
                poza granicami Polski (błędny układ współrzędnych). Pobierze ją ponownie z ULDK we
                właściwej strefie.
              </p>
            </div>
          )}
        </SectionCard>

        <ParcelConflictsModal
          open={conflictsOpen}
          groups={conflictGroups}
          onClose={() => setConflictsOpen(false)}
          onSubmit={submitConflictDecisions}
          submitting={conflictsSubmitting}
          error={conflictsError}
        />
        {connection.source_tender && (
          <SectionCard
            title="Wariant z przetargu ZE"
            action={
              <button
                type="button"
                className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                title={zeImportExpanded ? "Zwiń panel" : "Rozwiń panel"}
                onClick={() => setZeImportExpandedOverride(!zeImportExpanded)}
              >
                {zeImportExpanded ? (
                  <ChevronUp size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
              </button>
            }
            bodyClassName={zeImportExpanded ? undefined : "hidden"}
          >
            <div className="space-y-3">
              <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
                Skopiuj mapę narysowaną w przetargu{" "}
                <Link
                  to={`/ze-tenders/${connection.source_tender}`}
                  className="text-[var(--color-primary)] hover:underline"
                >
                  {connection.source_tender_info?.title || "ZE"}
                </Link>{" "}
                jako nowy wariant trasy projektu (działki zostaną wyznaczone
                automatycznie).
              </p>
              <label className="block">
                <span className="section-label">Wariant przetargu</span>
                <select
                  className="input-base mt-1"
                  value={importZeVariantId}
                  onChange={(e) => setImportZeVariantId(e.target.value)}
                >
                  <option value="">— wybierz —</option>
                  {(tenderVariants ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      Wariant {v.label}
                      {v.note ? ` — ${v.note}` : ""} · {v.lines_count} linii,{" "}
                      {v.objects_count} obiektów
                    </option>
                  ))}
                </select>
              </label>
              {hasPresets && (
                <label className="block">
                  <span className="section-label">Warstwy mapy (preset)</span>
                  <select
                    className="input-base mt-1"
                    value={importPresetId}
                    onChange={(e) => setImportPresetId(e.target.value)}
                  >
                    <option value="">Warstwy przetargu (bez zmian)</option>
                    {activePresets.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <span className="mt-0.5 block text-[11px] text-[var(--color-text-muted)]">
                    Pierwsza wersja mapy powstanie na warstwach wybranego presetu
                    (dopasowanie po typie obiektu).
                  </span>
                </label>
              )}
              <label className="block">
                <span className="section-label">
                  Oznaczenie w projekcie (opcjonalnie)
                </span>
                <input
                  className="input-base mt-1 text-xs py-1 w-20"
                  placeholder="auto"
                  maxLength={8}
                  value={importLabel}
                  onChange={(e) => setImportLabel(e.target.value)}
                />
              </label>
              {importError && (
                <p className="text-[11px] font-mono text-[var(--color-primary)]">
                  {importError}
                </p>
              )}
              <Button
                className="w-full"
                disabled={!importZeVariantId || taskBusy}
                loading={importPending || taskBusy}
                onClick={doImportFromTender}
              >
                Kopiuj jako nowy wariant
              </Button>
            </div>
          </SectionCard>
        )}

        <SectionCard
          title="Mapa (DXF)"
          action={<SupportedLayersInfo presetId={layerPresetId} />}
        >
          <div className="space-y-3">
            <label className="block">
              <span className="section-label">Wariant</span>
              <div className="mt-1 flex items-center gap-2">
                <select
                  className="input-base flex-1"
                  value={variantId}
                  onChange={(e) => setVariantId(e.target.value)}
                >
                  <option value="">— wybierz —</option>
                  {(variants ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      Wariant {v.label}
                      {v.is_binding ? " · obowiązujący" : ""}
                    </option>
                  ))}
                </select>
                {selectedVariant?.can_delete && (
                  <button
                    type="button"
                    title={`Usuń wariant ${selectedVariant.label}`}
                    disabled={deleteVariant.isPending}
                    onClick={() => { setVariantDeleteError(null); setVariantToDelete(selectedVariant); }}
                    className="shrink-0 p-1.5 border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] hover:border-[var(--color-danger)] transition-colors disabled:opacity-30"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </label>

            {/* Jawny wybór wariantu obowiązującego — z niego liczą się statusy działek,
                mapy zakresu, opisy ENERGA i wszystkie „sprawy". Bez wyboru (kilka
                wariantów) każdy ma własną „aktualną" wersję → niejednoznaczne. */}
            {multiVariant && (
              <div
                className={`p-2 border text-xs ${
                  anyBinding
                    ? "border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                    : "border-amber-300 bg-amber-50 dark:bg-amber-500/10"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="section-label inline-flex items-center gap-1">
                    <Anchor size={12} /> Wariant obowiązujący
                  </span>
                  {canManageRoutes ? (
                    <select
                      className="input-base !w-auto text-xs !py-0.5"
                      value={bindingVariantId}
                      disabled={setBindingVariant.isPending}
                      onChange={(e) => { if (e.target.value) setBindingVariant.mutate(e.target.value); }}
                    >
                      {!anyBinding && <option value="">— wybierz —</option>}
                      {(variants ?? []).map((v) => (
                        <option key={v.id} value={v.id}>Wariant {v.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="font-mono">
                      {anyBinding ? `Wariant ${(variants ?? []).find((v) => v.is_binding)?.label}` : "— nie ustawiony —"}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                  {anyBinding
                    ? "Na tym wariancie liczą się statusy działek, mapy zakresu, opisy ENERGA i sprawy."
                    : "Żaden wariant nie jest obowiązujący — dopóki nie wybierzesz, system liczy z ostatnio wgranej wersji (może pokazać kilka „aktualnych”)."}
                </p>
              </div>
            )}

            <Modal
              open={!!variantToDelete}
              onClose={() => { setVariantToDelete(null); setVariantDeleteError(null); }}
              title="UWAGA"
              size="sm"
            >
              {variantToDelete && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle
                      size={24}
                      className="shrink-0 mt-0.5 text-[var(--color-danger)]"
                    />
                    <div className="space-y-2 text-sm text-[var(--color-text-secondary)]">
                      <p className="font-bold text-[var(--color-text-primary)]">
                        Zamierzasz trwale usunąć wariant {variantToDelete.label}.
                      </p>
                      <p>
                        Ta operacja <strong>nieodwracalnie</strong> skasuje{" "}
                        <strong>
                          wszystkie wersje trasy tego wariantu (
                          {variantToDelete.versions.length})
                        </strong>{" "}
                        wraz z plikami DXF, podglądami, wyznaczonymi odcinkami na
                        działkach oraz wygenerowanymi formatkami planu
                        sytuacyjnego. Powiązania uzgodnień z właścicielami dla
                        tych wersji zostaną wyczyszczone.
                      </p>
                      {variantToDelete.versions.some((v) => v.is_current) && (
                        <p className="font-semibold text-[var(--color-danger)]">
                          Ten wariant zawiera aktualną (bieżącą) wersję trasy
                          projektu.
                        </p>
                      )}
                      <p className="font-semibold text-[var(--color-text-primary)]">
                        Tej operacji nie można cofnąć.
                      </p>
                    </div>
                  </div>
                  {variantDeleteError && (
                    <p className="text-xs font-mono text-[var(--color-danger)]">
                      {variantDeleteError}
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => { setVariantToDelete(null); setVariantDeleteError(null); }}
                      disabled={deleteVariant.isPending}
                    >
                      Anuluj
                    </Button>
                    <Button
                      variant="danger"
                      loading={deleteVariant.isPending}
                      onClick={() => {
                        setVariantDeleteError(null);
                        deleteVariant.mutate(variantToDelete.id, {
                          onSuccess: () => {
                            if (variantId === variantToDelete.id) setVariantId("");
                            setVariantToDelete(null);
                          },
                          onError: (e) =>
                            setVariantDeleteError(apiErr(e, "Nie udało się usunąć wariantu.")),
                        });
                      }}
                    >
                      Usuń wariant {variantToDelete.label}
                    </Button>
                  </div>
                </div>
              )}
            </Modal>

            <div className="flex gap-2">
              <input
                className="input-base text-xs py-1 w-20"
                placeholder="A / B"
                value={newVariantLabel}
                onChange={(e) => setNewVariantLabel(e.target.value)}
              />
              <Button
                size="sm"
                variant="ghost"
                disabled={!newVariantLabel}
                onClick={() =>
                  createVariant.mutate(newVariantLabel, {
                    onSuccess: (v) => {
                      setVariantId(v.id);
                      setNewVariantLabel("");
                    },
                  })
                }
              >
                + Nowy wariant
              </Button>
            </div>

            <label className="block">
              <span className="section-label">Preset warstw mapy</span>
              {hasPresets ? (
                <>
                  <select
                    className="input-base mt-1"
                    value={layerPresetId}
                    onChange={(e) => setLayerPresetId(e.target.value)}
                  >
                    <option value="">— wybierz preset —</option>
                    {activePresets.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.line_count + p.object_count} warstw)
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mt-1">
                    Wybierz, wg jakiego presetu nazwane są warstwy w Twoim DXF.{" "}
                    <Link
                      to="/power-projects/presety"
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      Zarządzaj presetami
                    </Link>
                  </p>
                </>
              ) : (
                <p className="text-[11px] font-mono text-amber-600 mt-1">
                  Brak presetów warstw.{" "}
                  <Link to="/power-projects/presety" className="underline">
                    Dodaj preset
                  </Link>
                  , aby system rozpoznał trasę i obiekty w DXF.
                </p>
              )}
            </label>

            <div>
              <span className="section-label">Plik DXF</span>
              <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mb-1.5">
                Wgraj jeden plik DXF — zawiera całą mapę (trasę i mapę
                zasadniczą jako warstwy).
              </p>
              <FileDropZone
                files={dxfFile ? [dxfFile] : []}
                onFilesChange={(f) => setDxfFile(f[0] ?? null)}
                accept=".dxf"
              />
              {dxfInvalid && (
                <p className="text-[11px] font-mono text-[var(--color-primary)] mt-1">
                  Wybierz plik DXF (rozszerzenie .dxf).
                </p>
              )}
            </div>

            <label className="block">
              <span className="section-label">Komentarz (opcjonalnie)</span>
              <textarea
                className="input-base mt-1 text-xs"
                rows={2}
                maxLength={500}
                placeholder="np. korekta trasy po uwagach gestora"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
                Zapisany z datą i autorem; widoczny w „Historia postępów".
              </p>
            </label>

            <label className="flex items-start gap-2 text-[11px] text-[var(--color-text-secondary)] cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-[var(--color-primary)] mt-0.5"
                checked={askBeforeUpload}
                onChange={(e) => toggleAskBeforeUpload(e.target.checked)}
              />
              <span>
                Pokaż raport warstw przed wysłaniem — zdecydujesz w monicie,
                czy mapa ma zostać wgrana.
              </span>
            </label>

            {validationError && (
              <p className="text-[11px] font-mono text-[var(--color-primary)]">
                {validationError}
              </p>
            )}

            <Button
              className="w-full"
              disabled={!uploadReady}
              loading={upload.isPending || validating}
              onClick={doUpload}
            >
              Wgraj wersję
            </Button>
          </div>
        </SectionCard>

        {taskId && (
          <div ref={taskLogRef}>
          <SectionCard title="Przetwarzanie">
            <div className="flex items-start gap-2 mb-1.5">
              {(!task.data || ["pending", "running"].includes(task.data.status)) && (
                <Spinner />
              )}
              <p className="text-xs font-mono">
                {!task.data ? (
                  <span className="text-[var(--color-text-secondary)]">
                    Uruchamiam… za chwilę pojawi się postęp na żywo.
                  </span>
                ) : task.data.status === "success" ? (
                  <span className="text-emerald-600">
                    ✓ Gotowe — mapa i działki są na miejscu.
                  </span>
                ) : task.data.status === "failure" ? (
                  <span className="text-[var(--color-primary)]">
                    {taskErrorLine(task.data.error)
                      ? `Błąd przetwarzania: ${taskErrorLine(task.data.error)}`
                      : "Błąd przetwarzania — sprawdź log poniżej."}
                  </span>
                ) : (
                  <span className="text-[var(--color-text-secondary)]">
                    Serwer pracuje… Postęp na żywo poniżej.
                  </span>
                )}
              </p>
            </div>
            <pre className="whitespace-pre-wrap max-h-40 overflow-y-auto text-[11px] font-mono text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] p-2 rounded">
              {/* On failure keep the live log AND the reason — the log is where the
                  step-by-step story is; dropping it for a one-line error hid what
                  actually got done before the failure. */}
              {(task.data?.status === "failure"
                ? [task.data.log, task.data.error].filter(Boolean).join("\n")
                : task.data?.log) || "…"}
            </pre>
          </SectionCard>
          </div>
        )}
          </>
        )}
        </div>
      </div>
      {validation && (
        <DxfValidationModal
          report={validation}
          fileName={dxfFile?.name}
          onCancel={() => setValidation(null)}
          onAccept={() => {
            setValidation(null);
            startUpload();
          }}
        />
      )}
    </div>

    {/* Formatki na pełnej szerokości pod mapą — więcej miejsca na listę zestawów.
        Na projekcie prowadzonym przez firmę sekcji nie ma (decyzja usera 2026-10-08). */}
    {!led && (
      <FormatkiSectionCard
        connection={connection}
        selectedVersion={selectedVersion ?? undefined}
      />
    )}

    {/* Miniatura kliknięta w dymku na mapie — ten sam lightbox (podpisy, nazwy plików,
        nawigacja strzałkami), którym materiał ogląda się w panelu MDCP po prawej. */}
    {lightboxIndex !== null && layerPhotos.length > 0 && (
      <MdcpPhotoLightbox
        photos={layerPhotos}
        index={Math.min(lightboxIndex, layerPhotos.length - 1)}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    )}
    </div>
  );
}

/** Event history of the clicked parcel, shown in the right-hand column while its map
 *  popup is open (replacing the usual cards). Same ProjectEvent feed as the parcel
 *  drawer in "Działki", rendered as a compact date/event table. */
// Urgency order for the collapsed WPW chip: problems beat "in progress" beats "not started";
// a secured consent/decision only shows when nothing above it is pending on any owner.
const WPW_SUMMARY_PRIORITY: WpwHandover["declared_status"][] = [
  "problem_refusal",
  "problem_impossible",
  "in_progress",
  "collected_in_field",
  "not_started",
  "decision_art124",
  "consent_written",
];

const PROC_SCOPE_SHORT: Record<Proceeding["scope"], string> = {
  office: "urzędowe",
  owner: "WPW",
  technical: "techniczne",
  notification: "zawiadomienie",
  letter: "list",
};

const sprawyPlural = (n: number): string => {
  if (n === 1) return "sprawa";
  const few = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
  return few ? "sprawy" : "spraw";
};

/** Latest non-cancelled zamówienie covering the parcel (cancelled only when nothing else). */
function latestOrder<T extends { created_at: string; status: string }>(
  orders: T[],
): T | null {
  const pool = orders.filter((o) => o.status !== "cancelled");
  const list = pool.length ? pool : orders;
  return list.length
    ? list.reduce((a, b) => (b.created_at > a.created_at ? b : a))
    : null;
}

/** Collapsible "Sprawy działki" card for the map's right column: the header row is a quick
 *  summary (case counts, last wniosek dispatch date, WPW / wypis / MPZP status) that is
 *  always visible; expanding reveals every sprawa with its dates, per-owner WPW statuses
 *  and the wypis/MPZP orders covering the parcel. Feeds entirely off data MapTab already
 *  fetches — no extra requests. */
function ParcelCasesCard({
  parcel,
  proceedings,
  wpwByOwner,
}: {
  parcel: Parcel;
  proceedings: Proceeding[];
  wpwByOwner: Map<string, WpwHandover>;
}) {
  // Persisted like the other map panels: switching tabs remounts MapTab and must not
  // fold the section back if the user works with it open.
  const [expanded, setExpanded] = usePersistentToggle(
    "powerProjects.map.parcelCasesExpanded",
    false,
  );
  const open = proceedings.filter((pr) => pr.status === "open");
  const sentDates = proceedings
    .map(lastApplicationSentDate)
    .filter((d): d is string => !!d)
    .sort();
  const lastSent = sentDates[sentDates.length - 1];
  const handovers = parcel.owners
    .map((o) => wpwByOwner.get(o.id))
    .filter((h): h is WpwHandover => !!h);
  const wpwSummary = handovers.length
    ? WPW_SUMMARY_PRIORITY.find((s) =>
        handovers.some((h) => h.declared_status === s),
      )
    : undefined;
  const wypis = latestOrder(parcel.wypis_orders ?? []);
  const mpzp = latestOrder(parcel.mpzp_orders ?? []);
  const empty =
    proceedings.length === 0 && handovers.length === 0 && !wypis && !mpzp;

  const chip = "inline-flex items-center gap-1 whitespace-nowrap";
  const muted = "text-[var(--color-text-muted)]";

  return (
    <SectionCard
      title={`Sprawy działki ${parcel.teryt ?? ""}`}
      action={
        <button
          type="button"
          className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          title={expanded ? "Zwiń sprawy" : "Rozwiń sprawy"}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      }
    >
      {empty ? (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
          Brak spraw dla tej działki.
        </p>
      ) : (
        <>
          {/* Quick summary — always visible, also while collapsed. */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px] text-[var(--color-text-primary)]">
            {proceedings.length > 0 && (
              <span className={chip}>
                {proceedings.length} {sprawyPlural(proceedings.length)}
                {open.length > 0 && (
                  <b className="text-[#2563eb]">({open.length} otwarte)</b>
                )}
              </span>
            )}
            {lastSent && (
              <span className={`${chip} ${muted}`}>
                ost. wniosek: <b className="text-[var(--color-text-primary)]">{fmtDate(lastSent)}</b>
              </span>
            )}
            {wpwSummary && (
              <span
                className={chip}
                style={{ color: WPW_DECLARED_STATUS_COLOR[wpwSummary] }}
              >
                WPW: {WPW_DECLARED_STATUS_LABEL[wpwSummary]}
              </span>
            )}
            {wypis && (
              <span className={`${chip} ${muted}`}>
                wypis: {wypis.status_display}
              </span>
            )}
            {mpzp && (
              <span className={`${chip} ${muted}`}>
                MPZP: {mpzp.status_display}
              </span>
            )}
          </div>

          {expanded && (
            <div className="mt-2 space-y-3 border-t border-[var(--color-border)] pt-2 text-xs">
              {proceedings.length > 0 && (
                <div>
                  <div className="section-label mb-1">Postępowania</div>
                  <ul className="space-y-1.5">
                    {proceedings.map((pr) => {
                      const isClosed = pr.status === "closed";
                      const color = isClosed
                        ? (PROC_OUTCOME_COLOR[pr.outcome] ?? "#6b7280")
                        : "#2563eb";
                      const sent = lastApplicationSentDate(pr);
                      return (
                        <li key={pr.id} className="leading-snug">
                          <span
                            className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full align-middle"
                            style={{ background: color }}
                          />
                          <span className="font-mono text-[10px] uppercase text-[var(--color-text-muted)]">
                            {PROC_SCOPE_SHORT[pr.scope]}
                          </span>{" "}
                          <span className="font-medium">{pr.type_name}</span>
                          {pr.type_note && (
                            <span className={muted}> — {pr.type_note}</span>
                          )}
                          <span className="font-semibold" style={{ color }}>
                            {" · "}
                            {isClosed
                              ? `${pr.outcome_display}${pr.closed_at ? ` · ${fmtDate(pr.closed_at)}` : ""}`
                              : "otwarte"}
                          </span>
                          {(sent || (!isClosed && pr.kpa_deadline)) && (
                            <div className={`ml-3.5 text-[11px] ${muted}`}>
                              {sent && <>wniosek wysłany {fmtDate(sent)}</>}
                              {!isClosed && pr.kpa_deadline && (
                                <span
                                  className={
                                    pr.kpa_overdue ? "text-[#dc2626]" : undefined
                                  }
                                >
                                  {sent ? " · " : ""}termin KPA{" "}
                                  {fmtDate(pr.kpa_deadline)}
                                </span>
                              )}
                            </div>
                          )}
                          {isClosed && pr.closing_note && (
                            <div className="ml-3.5 text-[11px] italic whitespace-pre-wrap text-[var(--color-text-secondary)]">
                              {pr.closing_note}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {handovers.length > 0 && (
                <div>
                  <div className="section-label mb-1">WPW — zgody właścicielskie</div>
                  <ul className="space-y-1">
                    {handovers.map((h) => (
                      <li key={h.id} className="leading-snug">
                        {h.owner.full_name}
                        <span
                          className="font-semibold"
                          style={{
                            color: WPW_DECLARED_STATUS_COLOR[h.declared_status],
                          }}
                        >
                          {" · "}
                          {WPW_DECLARED_STATUS_LABEL[h.declared_status]}
                        </span>
                        {h.declared_status_at && (
                          <span className={muted}>
                            {" · "}
                            {fmtDate(h.declared_status_at)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(parcel.wypis_orders?.length ?? 0) > 0 && (
                <div>
                  <div className="section-label mb-1">Wypisy EGiB</div>
                  <ul className="space-y-1">
                    {parcel.wypis_orders.map((o) => (
                      <li key={o.id} className="leading-snug">
                        <Link
                          to={`/power-projects/wypisy/${o.id}`}
                          className="font-mono text-[var(--color-primary)] hover:underline"
                        >
                          {o.number}
                        </Link>
                        <span> · {o.status_display}</span>
                        {o.sent_at && (
                          <span className={muted}> · wysłano {fmtDate(o.sent_at)}</span>
                        )}
                        {o.received_at && (
                          <span className={muted}> · otrzymano {fmtDate(o.received_at)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(parcel.mpzp_orders?.length ?? 0) > 0 && (
                <div>
                  <div className="section-label mb-1">Wypis MPZP</div>
                  <ul className="space-y-1">
                    {parcel.mpzp_orders.map((o) => (
                      <li key={o.id} className="leading-snug">
                        <Link
                          to={`/power-projects/mpzp/${o.id}`}
                          className="font-mono text-[var(--color-primary)] hover:underline"
                        >
                          {o.number}
                        </Link>
                        <span> · {o.status_display}</span>
                        {o.obtained_manually && (
                          <span className={muted} title="Wypis pozyskany ręcznie poza ELGW5 — same pliki, bez wniosku i wysyłki.">
                            {" · "}pozyskany ręcznie
                          </span>
                        )}
                        {o.sent_at && (
                          <span className={muted}> · wysłano {fmtDate(o.sent_at)}</span>
                        )}
                        {o.received_at && (
                          <span className={muted}> · otrzymano {fmtDate(o.received_at)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

function ParcelHistoryCard({
  connectionId,
  parcel,
  onClose,
}: {
  connectionId: string;
  parcel: Parcel;
  onClose: () => void;
}) {
  const { data: events, isLoading } = useProjectEvents(connectionId, {
    parcel: parcel.id,
  });
  return (
    <SectionCard
      title={`Historia działki ${parcel.teryt ?? ""}`}
      action={
        <button
          type="button"
          onClick={onClose}
          title="Zamknij historię (odznacz działkę)"
          aria-label="Zamknij historię działki"
          className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        >
          <X size={14} />
        </button>
      }
    >
      {isLoading ? (
        <div className="py-4 flex justify-center">
          <Spinner />
        </div>
      ) : !events || events.length === 0 ? (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
          Brak zdarzeń dla tej działki.
        </p>
      ) : (
        <div className="max-h-[560px] overflow-y-auto">
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[var(--color-bg-surface)]">
              <tr className="border-b border-[var(--color-border)]">
                <th className="section-label py-1 pr-2">Data</th>
                <th className="section-label py-1">Zdarzenie</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr
                  key={e.id}
                  className="border-b border-[var(--color-border)] last:border-b-0 align-top transition-colors hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="py-1.5 pr-2 whitespace-nowrap font-mono text-[11px] tabular-nums text-[var(--color-text-secondary)]">
                    {fmtDate(e.occurred_at)}
                    <span className="block text-[10px] text-[var(--color-text-muted)]">
                      {fmtTime(e.occurred_at)}
                    </span>
                  </td>
                  <td className="py-1.5">
                    <span className="block font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                      {e.kind_display}
                    </span>
                    <p className="text-xs font-medium leading-snug text-[var(--color-text-primary)]">
                      {e.title}
                    </p>
                    {e.detail && (
                      <p className="mt-0.5 text-[11px] leading-snug whitespace-pre-wrap text-[var(--color-text-secondary)]">
                        {e.detail}
                      </p>
                    )}
                    {e.actor_name && (
                      <p className="mt-0.5 font-mono text-[10px] text-[var(--color-text-muted)]">
                        {e.actor_name}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/** The pre-upload "monit": layer report vs the preset — the user decides here
 *  whether the map actually gets uploaded (AKCEPTUJ) or not (ANULUJ). */
// "N pustych symboli" — INSERTs of empty blocks, shown apart from real points
// because CAD draws nothing for them.
function emptySymbolsLabel(n: number): string {
  if (n === 1) return "1 pusty symbol";
  const few = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
  return `${n} ${few ? "puste symbole" : "pustych symboli"}`;
}

function DxfValidationModal({
  report,
  fileName,
  onAccept,
  onCancel,
}: {
  report: DxfValidationReport;
  fileName?: string;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const statusBadge = (l: DxfValidationReport["layers"][number]) => {
    if (l.status === "objects") {
      const parts = [
        l.lines ? `${l.lines} linii` : null,
        l.points ? `${l.points} pkt` : null,
        l.other ? `${l.other} in.` : null,
        l.empty_symbols ? emptySymbolsLabel(l.empty_symbols) : null,
      ].filter(Boolean);
      return <span className="text-emerald-600">{parts.join(", ")}</span>;
    }
    if (l.status === "empty") return <span className="text-amber-600">pusta</span>;
    return <span className="text-[var(--color-text-muted)]">brak</span>;
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="card w-full max-w-md shadow-hard-red"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
            Walidacja mapy (DXF)
          </h3>
          {fileName && (
            <p className="text-[11px] font-mono text-[var(--color-text-muted)] mt-0.5 truncate">
              {fileName}
            </p>
          )}
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Wykryto{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {report.detected} z {report.preset_layers_total}
            </span>{" "}
            warstw z presetu
            {report.preset_name ? ` „${report.preset_name}"` : ""}, w tym{" "}
            {report.with_objects} z obiektami i {report.empty}{" "}
            {report.empty === 1 ? "pustą" : "pustych"}:
          </p>
          <ul className="max-h-56 overflow-y-auto text-xs font-mono divide-y divide-[var(--color-border)] border border-[var(--color-border)]">
            {report.layers.map((l) => (
              <li key={l.layer} className="flex items-center justify-between gap-3 px-2 py-1">
                <span
                  className={
                    l.status === "missing"
                      ? "text-[var(--color-text-muted)]"
                      : "text-[var(--color-text-primary)]"
                  }
                >
                  {l.layer}
                </span>
                {statusBadge(l)}
              </li>
            ))}
          </ul>
          {report.extra_layers.length > 0 && (
            <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
              Warstwy spoza presetu (nie zostaną rozpoznane):{" "}
              {report.extra_layers.slice(0, 10).map((l) => l.layer).join(", ")}
              {report.extra_layers.length > 10 &&
                ` … i ${report.extra_layers.length - 10} innych`}
            </p>
          )}
          {report.with_objects === 0 && (
            <p className="text-[11px] font-mono text-amber-600 flex items-start gap-1.5">
              <AlertTriangle size={13} className="shrink-0 mt-px" />
              Żadna warstwa presetu nie ma obiektów — system nie rozpozna trasy
              ani obiektów w tej mapie.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Anuluj
            </Button>
            <Button size="sm" onClick={onAccept}>
              Akceptuj i wgraj
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** How many of the newest versions stay expanded once the history collapses. */
const TIMELINE_TAIL = 4;

/** Timeline of route versions under the map — click to preview an older version. */
function RouteTimeline({
  versions,
  selectedId,
  onSelect,
  bindingAware = false,
  onDelete,
}: {
  versions: TimelineVersion[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  // Kilka wariantów → „aktualna" znaczy co innego: tylko wersja obowiązującego
  // wariantu liczy sprawy; pozostałe są bieżące tylko w swoim wariancie.
  bindingAware?: boolean;
  // Stages a version for deletion — the parent owns the "UWAGA" modal. The trash icon
  // shows only on versions whose variant the user may delete (variantCanDelete).
  onDelete?: (v: TimelineVersion) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (versions.length === 0) return null;
  const sorted = [...versions].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );

  const card = (v: TimelineVersion) => {
    const sel = v.id === selectedId;
    // Jedna „aktualna" liczy sprawy: bieżąca wersja obowiązującego wariantu.
    // Przy wielu wariantach bieżąca wersja nieobowiązującego to tylko „bieżąca w wariancie".
    const authoritative = v.is_current && (!bindingAware || v.variantBinding);
    const currentButNotAuthoritative = v.is_current && bindingAware && !v.variantBinding;
    const showTrash = !!onDelete && !!v.variantCanDelete;
    return (
      <div key={v.id} className="relative shrink-0 group">
        <button
          type="button"
          onClick={() => onSelect(v.id)}
          className={`w-[160px] text-left border px-2.5 py-2 transition-colors ${
            sel
              ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
              : "border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${
                authoritative ? "bg-[var(--color-primary)]"
                  : currentButNotAuthoritative ? "bg-amber-500"
                  : "bg-[var(--color-text-muted)]"
              }`}
            />
            <span className="font-mono font-bold text-sm text-[var(--color-text-primary)]">
              {v.variantLabel}
              {v.number}
            </span>
            {authoritative && (
              <span className="section-label inline-flex items-center gap-0.5" title="Z tej wersji liczą się statusy działek i sprawy">
                <Anchor size={10} /> aktualna
              </span>
            )}
            {currentButNotAuthoritative && (
              <span className="section-label text-amber-700" title="Bieżąca tylko w tym wariancie — nie liczy spraw, dopóki wariant nie jest obowiązujący">
                bieżąca w wariancie
              </span>
            )}
          </div>
          <p className="text-[11px] font-mono text-[var(--color-text-muted)] mt-1">
            {fmtDateTime(v.created_at)}
            {v.created_by_name ? ` · ${v.created_by_name}` : ""}
          </p>
          <p className="text-[11px] font-mono text-[var(--color-text-secondary)]">
            {v.length_m ? `${Math.round(v.length_m)} m` : "— brak trasy —"}
            {v.objects?.length ? ` · ${v.objects.length} ob.` : ""}
          </p>
          {/* Korytarz pokazujemy tylko wtedy, gdy różni się od sumy — czyli gdy linie
              leżą na sobie albo biegną równolegle. To liczba, z której idzie MDCP. */}
          {v.corridor_length_m != null &&
            v.length_m != null &&
            Math.round(v.length_m) !== Math.round(v.corridor_length_m) && (
              <p
                className="text-[11px] font-mono text-[var(--color-primary)]"
                title="Linie biegnące równolegle w jednym pasie liczą się raz. Ta długość idzie do zamówienia MDCP."
              >
                korytarz {Math.round(v.corridor_length_m)} m
              </p>
            )}
          {/* Pas 35 m w każdą stronę od trasy w hektarach — ilość, gdy geodeta rozlicza
              MDCP za hektar; nakładające się odcinki liczone raz. */}
          {v.corridor_area_ha != null && (
            <p
              className="text-[11px] font-mono text-[var(--color-text-muted)]"
              title="Powierzchnia pasa 35 m w każdą stronę od osi trasy (70 m szerokości), nakładające się odcinki liczone raz. Z niej idzie ilość przy MDCP rozliczanym za hektar."
            >
              pas 70 m: {v.corridor_area_ha.toFixed(2)} ha
            </p>
          )}
          {v.layer_preset_name && (
            <p
              className="text-[10px] text-[var(--color-text-muted)] truncate"
              title={v.layer_preset_name}
            >
              {v.layer_preset_name}
            </p>
          )}
          {v.upload_comment && (
            <p
              className="text-[10px] text-[var(--color-text-secondary)] mt-0.5 line-clamp-2"
              title={v.upload_comment}
            >
              „{v.upload_comment}"
            </p>
          )}
        </button>
        {v.dxf_file?.url && (
          <a
            href={v.dxf_file.url}
            download={v.dxf_file.original_filename}
            title={`Pobierz DXF (${v.dxf_file.original_filename})`}
            onClick={(e) => e.stopPropagation()}
            className={`absolute p-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-[var(--color-text-muted)] hover:text-[var(--color-primary)] ${showTrash ? "top-1 right-6" : "top-1 right-1"}`}
          >
            <Download size={12} />
          </a>
        )}
        {showTrash && (
          <button
            type="button"
            title={`Usuń wersję ${v.variantLabel}${v.number}`}
            onClick={() => onDelete?.(v)}
            className="absolute top-1 right-1 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    );
  };

  // Collapse the middle once the list grows: keep the first version on the left,
  // collapse everything between into a "+X others" chip, and show the 4 newest.
  // Stay expanded if the previewed version would otherwise be hidden in the middle.
  const hiddenSlice = sorted.slice(1, sorted.length - TIMELINE_TAIL);
  const selectedHidden = hiddenSlice.some((v) => v.id === selectedId);
  const collapsed = !expanded && !selectedHidden && hiddenSlice.length > 0;
  const hiddenCount = hiddenSlice.length;

  return (
    <SectionCard title="Historia wersji trasy">
      <div className="flex gap-3 overflow-x-auto pb-1">
        {collapsed ? (
          <>
            {card(sorted[0])}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              title={`Pokaż wszystkie wersje (${sorted.length})`}
              className="shrink-0 w-[64px] flex flex-col items-center justify-center gap-1 border border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              <MoreHorizontal size={16} />
              <span className="font-mono text-sm font-bold leading-none">
                +{hiddenCount}
              </span>
              <span className="text-[10px] leading-none">innych</span>
            </button>
            {sorted.slice(-TIMELINE_TAIL).map(card)}
          </>
        ) : (
          <>
            {sorted.map(card)}
            {expanded && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                title="Zwiń historię"
                className="shrink-0 w-[64px] flex items-center justify-center border border-dashed border-[var(--color-border)] text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] transition-colors"
              >
                Zwiń
              </button>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}

/** Per-layer breakdown of the uploaded DXF + how the preset maps each layer. */
function DxfLayerInspector({ version }: { version: RouteVersion }) {
  const rows = version.dxf_layers ?? [];
  const [showUnmapped, setShowUnmapped] = useState(false);

  const mapped = rows.filter(
    (r) => r.mapped_as === "line" || r.mapped_as === "object",
  );
  const unmapped = rows.filter(
    (r) => r.mapped_as !== "line" && r.mapped_as !== "object",
  );

  const counts = (r: DxfLayerStat) =>
    [
      r.lines ? `${r.lines} linii` : "",
      r.points ? `${r.points} pkt` : "",
      r.other ? `${r.other} inne` : "",
      r.empty_symbols ? emptySymbolsLabel(r.empty_symbols) : "",
    ]
      .filter(Boolean)
      .join(" · ") || "0 encji";

  return (
    <SectionCard title="Warstwy w pliku DXF">
      <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mb-2">
        Co system znalazł w tym DXF i jak preset to mapuje. Warstwy
        „niezmapowane" są pomijane — dodaj je w{" "}
        <Link
          to="/power-projects/presety"
          className="text-[var(--color-primary)] hover:underline"
        >
          presetach warstw
        </Link>
        .
      </p>

      {mapped.length > 0 ? (
        <ul className="space-y-1">
          {mapped.map((r) => (
            <li
              key={r.layer}
              className="flex items-center justify-between gap-2 text-[11px] font-mono"
            >
              <span className="truncate">
                <span className="text-[var(--color-text-primary)]">
                  {r.layer || "0"}
                </span>
                <span className="text-[var(--color-text-muted)]">
                  {" "}
                  — {counts(r)}
                </span>
              </span>
              {r.mapped_as === "line" ? (
                <span className="text-emerald-600 whitespace-nowrap inline-flex items-center gap-0.5">
                  <Check size={12} />
                  trasa
                </span>
              ) : (
                <span className="text-emerald-600 whitespace-nowrap inline-flex items-center gap-0.5">
                  <Check size={12} />
                  obiekt
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-[var(--color-text-muted)] italic">
          Żadna warstwa nie została zmapowana.
        </p>
      )}

      {unmapped.length > 0 && (
        <div className="mt-2 border-t border-[var(--color-border)] pt-2">
          <button
            type="button"
            onClick={() => setShowUnmapped((o) => !o)}
            aria-expanded={showUnmapped}
            className="w-full flex items-center justify-between gap-2 text-[11px] text-amber-600 hover:text-amber-700 transition-colors"
          >
            <span className="inline-flex items-center gap-1">
              <AlertTriangle size={12} />
              Niezmapowane ({unmapped.length})
            </span>
            <ChevronDown
              size={14}
              className={`transition-transform ${showUnmapped ? "rotate-180" : ""}`}
            />
          </button>
          {showUnmapped && (
            <ul className="space-y-1 mt-1.5">
              {unmapped.map((r) => (
                <li
                  key={r.layer}
                  className="flex items-center justify-between gap-2 text-[11px] font-mono"
                >
                  <span className="truncate text-[var(--color-text-muted)]">
                    {r.layer || "0"} — {counts(r)}
                  </span>
                  <span className="text-amber-600 whitespace-nowrap inline-flex items-center gap-0.5">
                    <AlertTriangle size={12} />
                    niezmapowana
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SectionCard>
  );
}

/** "i" popover listing the DXF layers the parser recognizes for the chosen preset. */
function SupportedLayersInfo({ presetId }: { presetId: string }) {
  const [open, setOpen] = useState(false);
  const { data: layers, isLoading } = useDxfLayers(presetId || undefined, true);
  const lines = (layers ?? []).filter((c) => c.kind === "line");
  const objects = (layers ?? []).filter((c) => c.kind === "object");

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Jakie warstwy obsługuje system"
        title="Jakie warstwy obsługuje system"
        className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
      >
        <Info size={15} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 w-[280px] max-h-[340px] overflow-y-auto card shadow-lg p-3 text-left normal-case">
            <p className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)] mb-1">
              Obsługiwane warstwy DXF
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mb-2 normal-case">
              System rozpoznaje w DXF tylko warstwy z wybranego presetu.
              Pozostałe są ignorowane (zgłaszane jako „spoza katalogu"). Presety
              i warstwy edytujesz w{" "}
              <Link
                to="/power-projects/presety"
                className="text-[var(--color-primary)] hover:underline"
              >
                Presety warstw DXF
              </Link>
              .
            </p>
            {!presetId ? (
              <p className="text-[11px] font-mono text-amber-600">
                Wybierz najpierw preset, aby zobaczyć obsługiwane warstwy.
              </p>
            ) : isLoading ? (
              <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
                Wczytywanie…
              </p>
            ) : (layers?.length ?? 0) === 0 ? (
              <p className="text-[11px] font-mono text-amber-600">
                Ten preset nie ma jeszcze warstw — dodaj je w „Presety warstw
                DXF".
              </p>
            ) : (
              <div className="space-y-2">
                {lines.length > 0 && (
                  <div>
                    <p className="section-label">Trasa (linie)</p>
                    <ul className="mt-0.5 space-y-0.5">
                      {lines.map((c) => (
                        <li
                          key={c.id}
                          className="text-[11px] font-mono text-[var(--color-text-secondary)] break-words"
                        >
                          <span className="text-[var(--color-text-primary)]">
                            {c.layer_name}
                          </span>
                          {" → "}
                          {c.semantic_type}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {objects.length > 0 && (
                  <div>
                    <p className="section-label">Obiekty (punkty)</p>
                    <ul className="mt-0.5 space-y-0.5">
                      {objects.map((c) => (
                        <li
                          key={c.id}
                          className="text-[11px] font-mono text-[var(--color-text-secondary)] break-words"
                        >
                          <span className="text-[var(--color-text-primary)]">
                            {c.layer_name}
                          </span>
                          {" → "}
                          {c.semantic_type}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
